import { useState, useEffect, useRef, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link, useParams } from "react-router-dom";
import axios from "axios";
import { 
  LayoutDashboard, Users, QrCode, ArrowLeftRight, Gift, 
  Settings, LogOut, Search, Package, ShoppingBag,
  TrendingUp, AlertTriangle, Info, Bell,
  Download, ChevronLeft, ChevronRight, Wallet, History,
  ShieldCheck, Sparkles, Barcode, Ticket, Truck, Plus, X,
  Award, Crown, Star, Target, Users2, Building2, CreditCard,
  BarChart3, Shield, FileText, Zap, Edit, Trash2, HelpCircle,
  MessageCircle, Lightbulb, BookOpen, ChevronDown, RefreshCw
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Derive country from mobile number dial code (91 = India, 977 = Nepal)
const getCountryFromPhone = (phone) => {
  const digits = String(phone || "").replace(/\D/g, "");
  if (digits.startsWith("977")) return { name: "Nepal", flag: "🇳🇵" };
  if (digits.startsWith("91")) return { name: "India", flag: "🇮🇳" };
  return { name: "—", flag: "🌐" };
};

// Auth Context
const AuthContext = createContext(null);
const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      // Get permissions
      const permRes = await axios.get(`${API}/me/permissions`, { withCredentials: true });
      setPermissions(permRes.data.permissions || []);
    } catch (error) {
      setUser(null);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  const login = (userData) => setUser(userData);
  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch (e) {}
    setUser(null);
    setPermissions([]);
  };

  const hasPermission = (perm) => permissions.includes("*") || permissions.includes(perm) || permissions.some(p => p.endsWith(":*") && perm.startsWith(p.split(":")[0]));

  useEffect(() => { checkAuth(); }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, permissions, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, permission }) => {
  const { user, loading, hasPermission } = useAuth();
  const location = useLocation();

  if (loading) return <div className="loading-container" style={{ minHeight: '100vh' }}><div className="spinner"></div></div>;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (permission && !hasPermission(permission)) return <div className="page-content"><div className="card" style={{ textAlign: 'center', padding: '3rem' }}><Shield size={48} style={{ color: 'var(--error)', marginBottom: '1rem' }} /><h2>Access Denied</h2><p style={{ color: 'var(--text-muted)' }}>You don't have permission to view this page.</p></div></div>;
  return children;
};

// Auth Callback
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, checkAuth } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      if (sessionIdMatch) {
        try {
          const response = await axios.post(`${API}/auth/session`, { session_id: sessionIdMatch[1] }, { withCredentials: true });
          login(response.data);
          await checkAuth();
          navigate('/dashboard', { replace: true });
        } catch (error) { navigate('/login', { replace: true }); }
      } else { navigate('/dashboard', { replace: true }); }
    };
    processAuth();
  }, []);

  return <div className="loading-container" style={{ minHeight: '100vh' }}><div className="spinner"></div></div>;
};

// Login Page
const Login = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { if (user) navigate('/dashboard', { replace: true }); }, [user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/login`, { username, password }, { withCredentials: true });
      login(response.data);
      // Get permissions after login
      try {
        const permRes = await axios.get(`${API}/me/permissions`, { withCredentials: true });
        // Permissions are handled by AuthProvider
      } catch (e) {}
      window.location.href = '/dashboard';
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    }
    setLoading(false);
  };

  return (
    <div className="login-container">
      <div className="login-bg"></div>
      <div className="login-card slide-up">
        <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🎁</div>
        <img src="https://customer-assets.emergentagent.com/job_hp-adhesive/artifacts/wrwk26nv_logo-01.png" alt="HP Adhesives" className="login-logo"/>
        <h1 className="login-title">Rewards Admin Portal</h1>
        
        <form onSubmit={handleLogin} style={{ width: '100%' }}>
          {error && (
            <div style={{ background: '#FEE2E2', color: '#DC2626', padding: '0.75rem', borderRadius: '8px', fontSize: '0.875rem', marginBottom: '1rem', textAlign: 'center' }}>
              {error}
            </div>
          )}
          
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>Username</label>
            <input
              className="input"
              type="text"
              placeholder="Enter username"
              value={username}
              onChange={e => setUsername(e.target.value)}
              required
              autoComplete="username"
              data-testid="username-input"
              style={{ width: '100%' }}
            />
          </div>
          
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>Password</label>
            <input
              className="input"
              type="password"
              placeholder="Enter password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              data-testid="password-input"
              style={{ width: '100%' }}
            />
          </div>
          
          <button 
            type="submit" 
            className="btn btn-primary" 
            style={{ width: '100%', padding: '0.875rem' }} 
            disabled={loading}
            data-testid="login-btn"
          >
            {loading ? 'Signing in...' : '🔐 Sign In'}
          </button>
        </form>
        
        <p style={{ marginTop: '1.5rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}>Manage carpenters, rewards & payouts</p>
      </div>
    </div>
  );
};

// Sidebar with RBAC
const Sidebar = () => {
  const { user, logout, hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', permission: null },
    { path: '/dashboard/analytics', icon: BarChart3, label: 'Analytics', permission: 'analytics:*' },
    { path: '/dashboard/users', icon: Users, label: 'Users', permission: 'users:read' },
    { path: '/dashboard/brands', icon: Building2, label: 'Brands', permission: 'brands:read' },
    { path: '/dashboard/sku-master', icon: Package, label: '📦 SKU Master', permission: 'skus:read' },
    { path: '/dashboard/qr-codes', icon: QrCode, label: '🔲 QR Codes', permission: 'coupons:read' },
    { path: '/dashboard/payouts', icon: Wallet, label: '💰 Payouts', permission: 'payouts:read' },
    { path: '/dashboard/gift-cards', icon: CreditCard, label: 'Gift Cards', permission: 'gift_cards:read' },
    { path: '/dashboard/scans', icon: QrCode, label: 'Scans', permission: 'orders:read' },
    { path: '/dashboard/transactions', icon: ArrowLeftRight, label: 'Ledger', permission: 'ledger:read' },
    { path: '/dashboard/orders', icon: Truck, label: 'Orders', permission: 'orders:read' },
    { path: '/dashboard/rewards-catalog', icon: ShoppingBag, label: '🎁 Rewards Catalog', permission: 'store:read' },
    { path: '/dashboard/schemes', icon: Gift, label: 'Schemes', permission: 'schemes:read' },
    { path: '/dashboard/gamification', icon: Award, label: 'Gamification', permission: 'gamification:read' },
    { path: '/dashboard/admin-users', icon: Shield, label: 'Admin Users', permission: '*' },
    { path: '/dashboard/settings', icon: Settings, label: '⚙️ Settings', permission: '*' },
    { path: '/dashboard/audit-logs', icon: FileText, label: 'Audit Logs', permission: 'ledger:read' },
    { path: '/dashboard/insights', icon: Sparkles, label: '✨ AI Insights', permission: null },
    { path: '/dashboard/help', icon: HelpCircle, label: '❓ Help Center', permission: null },
  ];

  const filteredItems = navItems.filter(item => !item.permission || hasPermission(item.permission));

  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        <img src="https://customer-assets.emergentagent.com/job_hp-adhesive/artifacts/wrwk26nv_logo-01.png" alt="HP Adhesives" className="sidebar-logo"/>
      </div>
      <nav className="sidebar-nav">
        {filteredItems.map((item) => (
          <Link key={item.path} to={item.path} className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}>
            <item.icon size={18} />
            {item.label}
          </Link>
        ))}
      </nav>
      <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--border-default)' }}>
        {user && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
            {user.picture ? <img src={user.picture} alt={user.name} className="avatar" /> : <div className="avatar" style={{ background: 'var(--hp-red)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 600 }}>{user.name?.[0]}</div>}
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.name}</div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{user.roles?.join(', ') || 'Admin'}</div>
            </div>
          </div>
        )}
        <button className="btn btn-ghost" style={{ width: '100%', justifyContent: 'flex-start' }} onClick={async () => { await logout(); navigate('/login'); }}><LogOut size={16} /> Logout</button>
      </div>
    </aside>
  );
};

const DashboardLayout = ({ children }) => <div style={{ display: 'flex' }}><Sidebar /><main className="main-content">{children}</main></div>;
const StatCard = ({ label, value, highlight, icon: Icon, trend }) => (
  <div className="card card-glow stat-card">
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <span className="stat-label">{label}</span>
      {Icon && <div style={{ width: 36, height: 36, borderRadius: 10, background: highlight ? 'linear-gradient(135deg, #FEF2F2, #FEF3C7)' : 'var(--bg-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon size={18} style={{ color: highlight ? 'var(--hp-red)' : 'var(--text-muted)' }} /></div>}
    </div>
    <span className={`stat-value ${highlight ? 'highlight' : ''}`}>{value}</span>
    {trend && <div className={`stat-change ${trend > 0 ? 'positive' : 'negative'}`}>{trend > 0 ? '↑' : '↓'} {Math.abs(trend)}% from last week</div>}
  </div>
);

// Dashboard Home
const DashboardHome = () => {
  const [stats, setStats] = useState(null);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, insightsRes] = await Promise.all([
          axios.get(`${API}/stats`, { withCredentials: true }),
          axios.get(`${API}/insights`, { withCredentials: true })
        ]);
        setStats(statsRes.data);
        setInsights(insightsRes.data.insights || []);
      } catch (e) {}
      setLoading(false);
    };
    fetchData();
  }, []);

  if (loading) return <DashboardLayout><div className="page-header"><h1 className="page-title">🎯 Dashboard</h1></div><div className="page-content"><div className="loading-container"><div className="spinner"></div></div></div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="page-header" style={{ background: 'linear-gradient(to right, var(--bg-primary), #FEF7ED)' }}>
        <h1 className="page-title">
          <span style={{ fontSize: '1.5rem', marginRight: '0.5rem' }}>👋</span>
          Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}!
        </h1>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginTop: '0.25rem' }}>Here's what's happening with your rewards program today</p>
      </div>
      <div className="page-content">
        {/* Hero Stats */}
        <div style={{ background: 'linear-gradient(135deg, #FEF2F2 0%, #FEF3C7 50%, #ECFDF5 100%)', borderRadius: 16, padding: '1.5rem', marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: '1rem' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2.5rem', fontWeight: 800, color: 'var(--hp-red)' }}>{stats?.users?.total?.toLocaleString() || 0}</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 600 }}>🎉 Happy Carpenters</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2.5rem', fontWeight: 800, color: 'var(--success)' }}>{stats?.points?.total_issued?.toLocaleString() || 0}</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 600 }}>⭐ Points Distributed</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2.5rem', fontWeight: 800, color: 'var(--warning)' }}>₹{((stats?.payouts?.total_amount || 0)).toLocaleString()}</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 600 }}>💰 Cashback Given</div>
          </div>
        </div>

        <div className="bento-grid bento-grid-4" style={{ marginBottom: '1.5rem' }}>
          <StatCard label="KYC Success Rate" value={`${stats?.users?.kyc_success_rate || 0}%`} icon={ShieldCheck} />
          <StatCard label="Today's Scans" value={stats?.scans?.today?.toLocaleString() || 0} highlight icon={QrCode} />
          <StatCard label="Coupons Available" value={stats?.coupons?.available?.toLocaleString() || 0} icon={Ticket} />
          <StatCard label="Pending Orders" value={stats?.orders?.pending?.toLocaleString() || 0} icon={Truck} highlight />
        </div>

        {insights.length > 0 && (
          <div style={{ marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Sparkles size={18} style={{ color: 'var(--accent-gold)' }} /> AI Insights & Recommendations
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {insights.map((insight, i) => (
                <div key={i} className={`insight-card ${insight.type}`}>
                  <div className="insight-icon">{insight.type === 'warning' && <AlertTriangle size={18} />}{insight.type === 'alert' && <Bell size={18} />}{insight.type === 'info' && <Info size={18} />}</div>
                  <div className="insight-content"><div className="insight-title">{insight.title}</div><div className="insight-message">{insight.message}</div></div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>Quick Actions</h2>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <Link to="/dashboard/analytics" className="btn btn-secondary"><BarChart3 size={16} /> 📊 Analytics</Link>
            <Link to="/dashboard/users" className="btn btn-secondary"><Users size={16} /> 👥 Users</Link>
            <Link to="/dashboard/gift-cards" className="btn btn-secondary"><CreditCard size={16} /> 🎁 Gift Cards</Link>
            <Link to="/dashboard/orders" className="btn btn-secondary"><Truck size={16} /> 📦 Orders</Link>
            <button className="btn btn-primary" onClick={async () => { await axios.post(`${API}/seed`, {}, { withCredentials: true }); window.location.reload(); }}>✨ Seed Demo Data</button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

// Analytics Page
const AnalyticsPage = () => {
  const [data, setData] = useState(null);
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [overviewRes, predictRes] = await Promise.all([
          axios.get(`${API}/analytics/overview`, { withCredentials: true }),
          axios.get(`${API}/analytics/predictions`, { withCredentials: true })
        ]);
        setData(overviewRes.data);
        setPredictions(predictRes.data.predictions || []);
      } catch (e) {}
      setLoading(false);
    };
    fetchData();
  }, []);

  if (loading) return <DashboardLayout><div className="page-header"><h1 className="page-title">Analytics</h1></div><div className="page-content"><div className="loading-container"><div className="spinner"></div></div></div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="page-header"><h1 className="page-title">Analytics & AI Predictions</h1></div>
      <div className="page-content">
        {/* Key Metrics */}
        <div className="bento-grid bento-grid-4" style={{ marginBottom: '2rem' }}>
          <StatCard label="Active Users (Week)" value={data?.users?.active_this_week || 0} icon={Users2} />
          <StatCard label="Engagement Rate" value={`${data?.users?.engagement_rate || 0}%`} icon={Target} />
          <StatCard label="Redemption Rate" value={`${data?.points?.redemption_rate || 0}%`} icon={TrendingUp} />
          <StatCard label="Orders This Month" value={data?.orders?.this_month || 0} icon={Truck} />
        </div>

        {/* AI Predictions */}
        {predictions.length > 0 && (
          <div style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><Sparkles size={20} style={{ color: 'var(--accent-gold)' }} /> AI Predictions & Recommendations</h2>
            <div style={{ display: 'grid', gap: '1rem' }}>
              {predictions.map((pred, i) => (
                <div key={i} className={`insight-card ${pred.priority === 'high' ? 'alert' : pred.priority === 'medium' ? 'warning' : 'info'}`}>
                  <div className="insight-icon"><Sparkles size={20} /></div>
                  <div className="insight-content">
                    <div className="insight-title">{pred.title}</div>
                    <div className="insight-message">{pred.message}</div>
                    {pred.action && <div style={{ marginTop: '0.5rem' }}><button className="btn btn-primary" style={{ fontSize: '0.75rem', padding: '0.375rem 0.75rem' }}>{pred.action}</button></div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top Items */}
        <div className="bento-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
          <div className="card">
            <h3 style={{ marginBottom: '1rem' }}>Top Redeemed Items</h3>
            {data?.top_items?.map((item, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--border-default)' }}>
                <span>{i + 1}. {item.name}</span>
                <span style={{ color: 'var(--text-muted)' }}>{item.orders} orders</span>
              </div>
            ))}
          </div>
          <div className="card">
            <h3 style={{ marginBottom: '1rem' }}>AI Alerts</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', background: 'var(--hp-red-dim)', borderRadius: '8px', marginBottom: '0.75rem' }}>
              <AlertTriangle size={20} style={{ color: 'var(--hp-red)' }} />
              <div><div style={{ fontWeight: 600 }}>Churn Risk Users</div><div style={{ fontSize: '1.25rem', color: 'var(--hp-red)' }}>{data?.ai_alerts?.churn_risk_users || 0}</div></div>
            </div>
            <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Low Stock Items ({data?.ai_alerts?.low_stock_items?.length || 0})</div>
            {data?.ai_alerts?.low_stock_items?.slice(0, 3).map((item, i) => (
              <div key={i} style={{ fontSize: '0.875rem', color: 'var(--text-muted)', padding: '0.25rem 0' }}>{item.name}: {item.stock} left</div>
            ))}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

// Gift Cards Page
const GiftCardsPage = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ count: 10, value: 100, expires_days: 365 });

  const fetchCards = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/gift-cards?limit=50`, { withCredentials: true });
      setCards(response.data.gift_cards || []);
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => { fetchCards(); }, []);

  const handleGenerate = async (e) => {
    e.preventDefault();
    await axios.post(`${API}/gift-cards/generate`, formData, { withCredentials: true });
    setShowForm(false);
    fetchCards();
  };

  const statusColors = { active: 'badge-success', partially_used: 'badge-warning', fully_used: 'badge-neutral', cancelled: 'badge-error' };

  return (
    <DashboardLayout>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 className="page-title">Gift Cards</h1>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}><Plus size={16} /> {showForm ? 'Cancel' : 'Generate Cards'}</button>
      </div>
      <div className="page-content">
        {showForm && (
          <div className="card" style={{ marginBottom: '2rem' }}>
            <h3 style={{ marginBottom: '1rem' }}>Generate Gift Card Batch</h3>
            <form onSubmit={handleGenerate}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                <div><label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>Number of Cards</label><input type="number" className="input" value={formData.count} onChange={(e) => setFormData({ ...formData, count: parseInt(e.target.value) })} min="1" max="1000" /></div>
                <div><label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>Points Value</label><input type="number" className="input" value={formData.value} onChange={(e) => setFormData({ ...formData, value: parseInt(e.target.value) })} /></div>
                <div><label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>Validity (Days)</label><input type="number" className="input" value={formData.expires_days} onChange={(e) => setFormData({ ...formData, expires_days: parseInt(e.target.value) })} /></div>
              </div>
              <button type="submit" className="btn btn-primary" style={{ marginTop: '1rem' }}>Generate {formData.count} Cards</button>
            </form>
          </div>
        )}

        <div className="table-container">
          {loading ? <div className="loading-container"><div className="spinner"></div></div> : (
            <table className="table">
              <thead><tr><th>Card Code</th><th>Value</th><th>Remaining</th><th>Status</th><th>Used By</th><th>Expires</th></tr></thead>
              <tbody>
                {cards.map((card) => (
                  <tr key={card.card_id}>
                    <td className="mono" style={{ fontWeight: 600 }}>{card.card_code}</td>
                    <td>{card.value} pts</td>
                    <td style={{ color: card.remaining_value > 0 ? 'var(--success)' : 'var(--text-muted)' }}>{card.remaining_value} pts</td>
                    <td><span className={`badge ${statusColors[card.status] || 'badge-neutral'}`}>{card.status}</span></td>
                    <td>{card.used_by || '-'}</td>
                    <td style={{ fontSize: '0.8rem' }}>{card.expires_at ? new Date(card.expires_at).toLocaleDateString() : '-'}</td>
                  </tr>
                ))}
                {cards.length === 0 && <tr><td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>No gift cards yet</td></tr>}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

// Brands Page
const BrandsPage = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', code: '', description: '' });

  const fetchBrands = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/brands`, { withCredentials: true });
      setBrands(response.data.brands || []);
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => { fetchBrands(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await axios.post(`${API}/brands`, formData, { withCredentials: true });
    setShowForm(false);
    setFormData({ name: '', code: '', description: '' });
    fetchBrands();
  };

  return (
    <DashboardLayout>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 className="page-title">Brand Management</h1>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}><Plus size={16} /> {showForm ? 'Cancel' : 'Add Brand'}</button>
      </div>
      <div className="page-content">
        {showForm && (
          <div className="card" style={{ marginBottom: '2rem' }}>
            <h3 style={{ marginBottom: '1rem' }}>Add New Brand</h3>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                <div><label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>Brand Name</label><input type="text" className="input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required /></div>
                <div><label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>Brand Code</label><input type="text" className="input" value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })} placeholder="e.g., HPA" /></div>
                <div style={{ gridColumn: '1/-1' }}><label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>Description</label><input type="text" className="input" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} /></div>
              </div>
              <button type="submit" className="btn btn-primary" style={{ marginTop: '1rem' }}>Create Brand</button>
            </form>
          </div>
        )}

        <div className="table-container">
          {loading ? <div className="loading-container"><div className="spinner"></div></div> : (
            <table className="table">
              <thead><tr><th>Brand ID</th><th>Name</th><th>Code</th><th>Conversion Rate</th><th>Status</th></tr></thead>
              <tbody>
                {brands.map((brand) => (
                  <tr key={brand.brand_id}>
                    <td className="mono" style={{ fontSize: '0.75rem' }}>{brand.brand_id}</td>
                    <td style={{ fontWeight: 600 }}>{brand.name}</td>
                    <td className="mono">{brand.code}</td>
                    <td>1 pt = ₹{brand.points_conversion_rate || 0.25}</td>
                    <td><span className={`badge ${brand.is_active ? 'badge-success' : 'badge-neutral'}`}>{brand.is_active ? 'Active' : 'Inactive'}</span></td>
                  </tr>
                ))}
                {brands.length === 0 && <tr><td colSpan="5" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>No brands yet. Create HP Adhesives brand!</td></tr>}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

// Gamification Admin Page
const GamificationAdminPage = () => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/gamification/leaderboard?limit=20`);
        setLeaderboard(response.data.leaderboard || []);
      } catch (e) {}
      setLoading(false);
    };
    fetchData();
  }, []);

  const tiers = [
    { name: 'Diamond', icon: '💠', min: 10000, color: '#00D4FF' },
    { name: 'Platinum', icon: '💎', min: 5000, color: '#E5E4E2' },
    { name: 'Gold', icon: '🥇', min: 2000, color: '#FFD700' },
    { name: 'Silver', icon: '🥈', min: 500, color: '#C0C0C0' },
    { name: 'Bronze', icon: '🥉', min: 0, color: '#CD7F32' },
  ];

  const badges = [
    { id: 'first_scan', name: 'First Scan', icon: '🎯', bonus: 10 },
    { id: 'points_100', name: 'Century Club', icon: '💯', bonus: 10 },
    { id: 'points_500', name: 'Half K Hero', icon: '🏆', bonus: 25 },
    { id: 'points_1000', name: 'Thousand Champion', icon: '👑', bonus: 50 },
    { id: 'first_redeem', name: 'First Redemption', icon: '🛍️', bonus: 10 },
    { id: 'referral_1', name: 'Connector', icon: '🤝', bonus: 25 },
  ];

  return (
    <DashboardLayout>
      <div className="page-header"><h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><Award size={24} style={{ color: 'var(--accent-gold)' }} /> Gamification</h1></div>
      <div className="page-content">
        <div className="bento-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '2rem' }}>
          {/* Tiers */}
          <div className="card">
            <h3 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><Crown size={18} /> Loyalty Tiers</h3>
            {tiers.map((tier, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: 'var(--bg-primary)', borderRadius: '8px', marginBottom: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ fontSize: '1.5rem' }}>{tier.icon}</span>
                  <div><div style={{ fontWeight: 600 }}>{tier.name}</div><div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{tier.min.toLocaleString()}+ points</div></div>
                </div>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>{tier.name === 'Bronze' ? '1x' : tier.name === 'Silver' ? '1.1x' : tier.name === 'Gold' ? '1.25x' : tier.name === 'Platinum' ? '1.5x' : '2x'}</div>
              </div>
            ))}
          </div>

          {/* Badges */}
          <div className="card">
            <h3 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><Star size={18} /> Available Badges</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
              {badges.map((badge, i) => (
                <div key={i} style={{ padding: '0.75rem', background: 'var(--bg-primary)', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.25rem' }}>{badge.icon}</div>
                  <div style={{ fontSize: '0.875rem', fontWeight: 600 }}>{badge.name}</div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--success)' }}>+{badge.bonus} pts</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Leaderboard */}
        <div className="card">
          <h3 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><Target size={18} /> Leaderboard</h3>
          <div className="table-container" style={{ border: 'none' }}>
            {loading ? <div className="loading-container"><div className="spinner"></div></div> : (
              <table className="table">
                <thead><tr><th>Rank</th><th>User</th><th>Points</th><th>Tier</th><th>Badges</th></tr></thead>
                <tbody>
                  {leaderboard.map((user) => (
                    <tr key={user.rank}>
                      <td style={{ fontSize: '1.25rem', fontWeight: 700 }}>{user.rank <= 3 ? ['🥇', '🥈', '🥉'][user.rank - 1] : `#${user.rank}`}</td>
                      <td style={{ fontWeight: 600 }}>{user.name}</td>
                      <td style={{ color: 'var(--hp-red)', fontWeight: 700 }}>{user.points?.toLocaleString()}</td>
                      <td>{user.tier_icon} {user.tier}</td>
                      <td>{user.badges_count} 🏅</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

// Admin Users Page
const AdminUsersPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [formData, setFormData] = useState({ username: '', password: '', name: '', email: '', role: 'admin' });
  const [error, setError] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin-users`, { withCredentials: true });
      setUsers(res.data.users || []);
    } catch (e) { 
      if (e.response?.status === 403) setError('Super admin access required');
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editUser) {
        await axios.put(`${API}/admin-users/${editUser.user_id}`, formData, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin-users`, formData, { withCredentials: true });
      }
      setShowForm(false);
      setEditUser(null);
      setFormData({ username: '', password: '', name: '', email: '', role: 'admin' });
      fetchData();
    } catch (e) {
      setError(e.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this admin user?')) return;
    try {
      await axios.delete(`${API}/admin-users/${userId}`, { withCredentials: true });
      fetchData();
    } catch (e) {
      alert(e.response?.data?.detail || 'Delete failed');
    }
  };

  const openEdit = (user) => {
    setEditUser(user);
    setFormData({
      username: user.username || '',
      password: '',
      name: user.name || '',
      email: user.email || '',
      role: user.role || 'admin',
      is_active: user.is_active !== false
    });
    setShowForm(true);
  };

  const isSuperAdmin = currentUser?.role === 'super_admin';

  return (
    <DashboardLayout>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 className="page-title"><Shield size={24} /> Admin Users</h1>
        {isSuperAdmin && (
          <button className="btn btn-primary" onClick={() => { setShowForm(!showForm); setEditUser(null); setFormData({ username: '', password: '', name: '', email: '', role: 'admin' }); }}>
            <Plus size={16} /> {showForm ? 'Cancel' : 'Add Admin'}
          </button>
        )}
      </div>
      <div className="page-content">
        {error && (
          <div className="card" style={{ background: '#FEE2E2', color: '#DC2626', marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        {!isSuperAdmin && (
          <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
            <Shield size={48} style={{ color: 'var(--warning)', marginBottom: '1rem' }} />
            <h3>Super Admin Access Required</h3>
            <p style={{ color: 'var(--text-muted)' }}>Only super admins can manage admin users.</p>
          </div>
        )}

        {isSuperAdmin && showForm && (
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ marginBottom: '1rem' }}>{editUser ? 'Edit Admin User' : 'Add Admin User'}</h3>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.8rem', fontWeight: 600 }}>Username *</label>
                  <input type="text" className="input" value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} required disabled={!!editUser} placeholder="lowercase username" />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.8rem', fontWeight: 600 }}>{editUser ? 'New Password' : 'Password *'}</label>
                  <input type="password" className="input" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required={!editUser} placeholder={editUser ? 'Leave blank to keep current' : 'Min 6 characters'} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.8rem', fontWeight: 600 }}>Full Name</label>
                  <input type="text" className="input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Display name" />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.8rem', fontWeight: 600 }}>Email</label>
                  <input type="email" className="input" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="admin@example.com" />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.8rem', fontWeight: 600 }}>Role</label>
                  <select className="input" value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })}>
                    <option value="admin">Admin</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>
                {editUser && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input type="checkbox" id="is_active" checked={formData.is_active !== false} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} />
                    <label htmlFor="is_active">Active</label>
                  </div>
                )}
              </div>
              <button type="submit" className="btn btn-primary" style={{ marginTop: '1rem' }}>{editUser ? 'Update' : 'Create'} Admin</button>
            </form>
          </div>
        )}

        {isSuperAdmin && (
          <div className="table-container">
            {loading ? <div className="loading-container"><div className="spinner"></div></div> : (
              <table className="table">
                <thead><tr><th>Username</th><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Last Login</th><th>Actions</th></tr></thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.user_id}>
                      <td style={{ fontWeight: 600, fontFamily: 'monospace' }}>{user.username}</td>
                      <td>{user.name}</td>
                      <td>{user.email}</td>
                      <td><span className={`badge ${user.role === 'super_admin' ? 'badge-warning' : 'badge-neutral'}`}>{user.role}</span></td>
                      <td><span className={`badge ${user.is_active !== false ? 'badge-success' : 'badge-error'}`}>{user.is_active !== false ? 'Active' : 'Inactive'}</span></td>
                      <td style={{ fontSize: '0.8rem' }}>{user.last_login ? new Date(user.last_login).toLocaleString() : '-'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button onClick={() => openEdit(user)} className="btn btn-secondary" style={{ padding: '0.25rem 0.5rem' }}><Edit size={14} /></button>
                          {user.user_id !== 'admin_super' && user.user_id !== currentUser?.user_id && (
                            <button onClick={() => handleDelete(user.user_id)} className="btn btn-secondary" style={{ padding: '0.25rem 0.5rem', color: 'var(--error)' }}><Trash2 size={14} /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

// Audit Logs Page
const AuditLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const response = await axios.get(`${API}/audit-logs`, { withCredentials: true });
        setLogs(response.data.logs || []);
      } catch (e) {}
      setLoading(false);
    };
    fetchLogs();
  }, []);

  return (
    <DashboardLayout>
      <div className="page-header"><h1 className="page-title">Audit Logs</h1></div>
      <div className="page-content">
        <div className="table-container">
          {loading ? <div className="loading-container"><div className="spinner"></div></div> : (
            <table className="table">
              <thead><tr><th>Timestamp</th><th>Action</th><th>Resource</th><th>User</th><th>Details</th></tr></thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.audit_id}>
                    <td style={{ fontSize: '0.8rem' }}>{new Date(log.timestamp).toLocaleString()}</td>
                    <td><span className="badge badge-neutral">{log.action}</span></td>
                    <td>{log.resource_type} / {log.resource_id}</td>
                    <td className="mono" style={{ fontSize: '0.75rem' }}>{log.user_id}</td>
                    <td style={{ fontSize: '0.75rem', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{JSON.stringify(log.details)}</td>
                  </tr>
                ))}
                {logs.length === 0 && <tr><td colSpan="5" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>No audit logs yet</td></tr>}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

// Simplified Other Pages
const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterState, setFilterState] = useState('');
  
  useEffect(() => {
    axios.get(`${API}/users`, { withCredentials: true })
      .then(r => setUsers(r.data.users || []))
      .finally(() => setLoading(false));
  }, []);
  
  // Get unique states for filter dropdown
  const uniqueStates = [...new Set(users.map(u => u.state).filter(Boolean))].sort();
  
  // Filter users
  const filteredUsers = users.filter(u => {
    const matchSearch = !search || 
      u.name?.toLowerCase().includes(search.toLowerCase()) ||
      u.phone?.includes(search) ||
      u.city?.toLowerCase().includes(search.toLowerCase());
    const matchState = !filterState || u.state === filterState;
    return matchSearch && matchState;
  });
  
  return (
    <DashboardLayout>
      <div className="page-header">
        <h1 className="page-title"><Users size={24} /> Registered Users</h1>
        <div className="page-actions" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <div className="input-with-icon" style={{ position: 'relative' }}>
            <Search size={16} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', opacity: 0.5 }} />
            <input 
              className="input" 
              placeholder="Search name, phone, city..." 
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ paddingLeft: '32px', width: '220px' }}
            />
          </div>
          <select 
            className="select" 
            value={filterState} 
            onChange={e => setFilterState(e.target.value)}
            style={{ width: '150px' }}
          >
            <option value="">All States</option>
            {uniqueStates.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>
      <div className="page-content">
        <div className="table-container" style={{ overflowX: 'auto' }}>
          {loading ? (
            <div className="loading-container"><div className="spinner"></div></div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Phone</th>
                  <th>Country</th>
                  <th>Name</th>
                  <th>Language</th>
                  <th>State</th>
                  <th>City</th>
                  <th>Pincode</th>
                  <th>KYC</th>
                  <th>Balance</th>
                  <th>Tier</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(u => (
                  <tr key={u.user_id}>
                    <td className="mono">{u.phone}</td>
                    <td data-testid={`user-country-${u.user_id}`}>
                      <span className="badge badge-neutral">{getCountryFromPhone(u.phone).flag} {getCountryFromPhone(u.phone).name}</span>
                    </td>
                    <td>{u.name || '-'}</td>
                    <td>
                      <span className="badge badge-info" style={{ textTransform: 'uppercase' }}>
                        {u.language || 'en'}
                      </span>
                    </td>
                    <td>{u.state || '-'}</td>
                    <td>{u.city || '-'}</td>
                    <td className="mono">{u.pincode || '-'}</td>
                    <td>
                      <span className={`badge ${u.kyc_status === 'verified' ? 'badge-success' : 'badge-warning'}`}>
                        {u.kyc_status || 'pending'}
                      </span>
                    </td>
                    <td style={{ fontWeight: 600 }}>{u.current_balance || 0}</td>
                    <td>{u.total_points_earned >= 2000 ? '🥇' : u.total_points_earned >= 500 ? '🥈' : '🥉'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!loading && filteredUsers.length === 0 && (
            <div className="empty-state" style={{ padding: '3rem', textAlign: 'center' }}>
              <Users size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
              <p style={{ color: 'var(--text-muted)' }}>No users found</p>
            </div>
          )}
        </div>
        <div style={{ marginTop: '1rem', fontSize: '0.875rem', color: 'var(--text-muted)' }}>
          Showing {filteredUsers.length} of {users.length} users
        </div>
      </div>
    </DashboardLayout>
  );
};
// ============== SKU MASTER PAGE (FULL CRUD) ==============
const SKUMasterPage = () => {
  const [skus, setSkus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ brands: [], categories: [] });
  const [search, setSearch] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [editModal, setEditModal] = useState(null);
  const [importModal, setImportModal] = useState(false);
  const [importData, setImportData] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchSkus = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (selectedBrand) params.append('brand', selectedBrand);
      if (selectedCategory) params.append('category', selectedCategory);
      const res = await axios.get(`${API}/sku-master?${params}`, { withCredentials: true });
      setSkus(res.data.skus || []);
      setFilters(res.data.filters || { brands: [], categories: [] });
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => { fetchSkus(); }, [search, selectedBrand, selectedCategory]);

  const saveSku = async () => {
    if (!editModal) return;
    setSaving(true);
    try {
      if (editModal.isNew) {
        await axios.post(`${API}/sku-master`, editModal, { withCredentials: true });
      } else {
        await axios.put(`${API}/sku-master/${editModal.sku_code}`, editModal, { withCredentials: true });
      }
      setEditModal(null);
      fetchSkus();
    } catch (e) { alert(e.response?.data?.detail || 'Error saving SKU'); }
    setSaving(false);
  };

  const deleteSku = async (sku_code) => {
    if (!window.confirm('Deactivate this SKU?')) return;
    try {
      await axios.delete(`${API}/sku-master/${sku_code}`, { withCredentials: true });
      fetchSkus();
    } catch (e) { alert('Error'); }
  };

  const handleImport = async () => {
    setSaving(true);
    try {
      const parsed = JSON.parse(importData);
      const skusArray = Array.isArray(parsed) ? parsed : [parsed];
      await axios.post(`${API}/sku-master/import`, { skus: skusArray }, { withCredentials: true });
      setImportModal(false);
      setImportData('');
      fetchSkus();
      alert('Import successful!');
    } catch (e) { alert('Invalid JSON or import error'); }
    setSaving(false);
  };

  return (
    <DashboardLayout>
      <div className="page-header">
        <h1 className="page-title">📦 SKU Master</h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn btn-secondary" onClick={() => setImportModal(true)}>📥 Import</button>
          <button className="btn btn-primary" onClick={() => setEditModal({ isNew: true, sku_code: '', name: '', brand: 'HP', category: 'Adhesives', points: 10, value: 0, mrp: 0, is_active: true })}>+ Add SKU</button>
        </div>
      </div>
      <div className="page-content">
        {/* Filters */}
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <div className="search-box" style={{ flex: 1, minWidth: '200px' }}>
            <Search size={16} />
            <input className="input" placeholder="Search SKU code or name..." value={search} onChange={e => setSearch(e.target.value)} style={{ paddingLeft: '2.5rem' }} />
          </div>
          <select className="input" style={{ width: 'auto' }} value={selectedBrand} onChange={e => setSelectedBrand(e.target.value)}>
            <option value="">All Brands</option>
            {filters.brands.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
          <select className="input" style={{ width: 'auto' }} value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)}>
            <option value="">All Categories</option>
            {filters.categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {/* Table */}
        <div className="table-container">
          {loading ? <div className="loading-container"><div className="spinner"></div></div> : (
            <table className="table">
              <thead>
                <tr>
                  <th>SKU Code</th>
                  <th>Name</th>
                  <th>Brand</th>
                  <th>Category</th>
                  <th>Points</th>
                  <th>MRP</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {skus.map(s => (
                  <tr key={s.sku_code}>
                    <td className="mono" style={{ fontWeight: 600 }}>{s.sku_code}</td>
                    <td>{s.name}</td>
                    <td>{s.brand}</td>
                    <td>{s.category}</td>
                    <td style={{ color: 'var(--success)', fontWeight: 700 }}>⭐ {s.points?.toFixed(2)}</td>
                    <td>₹{s.mrp?.toLocaleString()}</td>
                    <td><span className={`badge ${s.is_active ? 'badge-success' : 'badge-error'}`}>{s.is_active ? 'Active' : 'Inactive'}</span></td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button className="btn btn-ghost" onClick={() => setEditModal({ ...s })}><Edit size={14} /></button>
                        <button className="btn btn-ghost" style={{ color: 'var(--error)' }} onClick={() => deleteSku(s.sku_code)}><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Edit Modal */}
        {editModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div className="card" style={{ width: '100%', maxWidth: '500px', maxHeight: '90vh', overflow: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h3 style={{ margin: 0 }}>{editModal.isNew ? '➕ Add New SKU' : '✏️ Edit SKU'}</h3>
                <button onClick={() => setEditModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.5rem' }}>×</button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', display: 'block' }}>SKU Code *</label>
                  <input className="input" value={editModal.sku_code} onChange={e => setEditModal({ ...editModal, sku_code: e.target.value })} disabled={!editModal.isNew} placeholder="HPA_PRODUCT_SIZE" />
                </div>
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', display: 'block' }}>Product Name *</label>
                  <input className="input" value={editModal.name} onChange={e => setEditModal({ ...editModal, name: e.target.value })} placeholder="HP Fevicol SR 505 50ML" />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  <div>
                    <label style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', display: 'block' }}>Brand</label>
                    <input className="input" value={editModal.brand} onChange={e => setEditModal({ ...editModal, brand: e.target.value })} placeholder="HP" />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', display: 'block' }}>Category</label>
                    <input className="input" value={editModal.category} onChange={e => setEditModal({ ...editModal, category: e.target.value })} placeholder="Adhesives" />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
                  <div>
                    <label style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', display: 'block' }}>Points ⭐</label>
                    <input className="input" type="number" step="0.01" value={editModal.points} onChange={e => setEditModal({ ...editModal, points: parseFloat(e.target.value) || 0 })} />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', display: 'block' }}>Value (₹)</label>
                    <input className="input" type="number" step="0.01" value={editModal.value} onChange={e => setEditModal({ ...editModal, value: parseFloat(e.target.value) || 0 })} />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', display: 'block' }}>MRP (₹)</label>
                    <input className="input" type="number" step="0.01" value={editModal.mrp} onChange={e => setEditModal({ ...editModal, mrp: parseFloat(e.target.value) || 0 })} />
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input type="checkbox" id="is_active" checked={editModal.is_active} onChange={e => setEditModal({ ...editModal, is_active: e.target.checked })} />
                  <label htmlFor="is_active">Active</label>
                </div>
                <button className="btn btn-primary" style={{ width: '100%', marginTop: '1rem' }} onClick={saveSku} disabled={saving}>
                  {saving ? '⏳ Saving...' : '💾 Save SKU'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Import Modal */}
        {importModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div className="card" style={{ width: '100%', maxWidth: '600px', maxHeight: '90vh', overflow: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ margin: 0 }}>📥 Import SKU Master</h3>
                <button onClick={() => setImportModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.5rem' }}>×</button>
              </div>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
                Paste JSON array of SKUs. Required fields: <code>sku_code</code>, <code>name</code>, <code>points</code>
              </p>
              <textarea className="input" rows={12} value={importData} onChange={e => setImportData(e.target.value)} placeholder='[{"sku_code": "HP_904_UPVC_MD_50ML", "name": "HP 904 UPVC MD 50ML", "brand": "HP", "category": "Adhesives", "points": 10, "mrp": 50}]' style={{ fontFamily: 'monospace', fontSize: '0.75rem' }} />
              <button className="btn btn-primary" style={{ width: '100%', marginTop: '1rem' }} onClick={handleImport} disabled={saving}>
                {saving ? '⏳ Importing...' : '📥 Import SKUs'}
              </button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

// ============== QR CODE GENERATION PAGE ==============
const QRCodesPage = () => {
  const [batches, setBatches] = useState([]);
  const [skus, setSkus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generateModal, setGenerateModal] = useState(false);
  const [selectedSku, setSelectedSku] = useState('');
  const [count, setCount] = useState(1000);
  const [generating, setGenerating] = useState(false);
  const [exportModal, setExportModal] = useState(null);
  const [previewModal, setPreviewModal] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(null);
  // Pagination + summary (#4)
  const [page, setPage] = useState(1);
  const [totalBatches, setTotalBatches] = useState(0);
  const [summary, setSummary] = useState({ total_batches: 0, total_codes: 0, total_used: 0, total_available: 0 });
  const LIMIT = 20;
  // Coupon search (#4)
  const [searchCode, setSearchCode] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);
  // Delete batch (#2)
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  // Edit points value (#5)
  const [editModal, setEditModal] = useState(null);
  const [editPoints, setEditPoints] = useState(0);
  const [savingPoints, setSavingPoints] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [batchesRes, skusRes] = await Promise.all([
        axios.get(`${API}/qr-codes/batches?page=${page}&limit=${LIMIT}`, { withCredentials: true }),
        axios.get(`${API}/sku-master`, { withCredentials: true })
      ]);
      setBatches(batchesRes.data.batches || []);
      setTotalBatches(batchesRes.data.total || 0);
      if (batchesRes.data.summary) setSummary(batchesRes.data.summary);
      setSkus(skusRes.data.skus || []);
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [page]);

  const searchCoupon = async () => {
    const code = searchCode.trim();
    if (!code) return;
    setSearching(true); setSearchResult(null);
    try {
      const res = await axios.get(`${API}/qr-codes/coupon/${encodeURIComponent(code)}`, { withCredentials: true });
      setSearchResult({ found: true, coupon: res.data.coupon });
    } catch (e) {
      setSearchResult({ found: false, error: e.response?.status === 404 ? 'Coupon code not found' : 'Search failed' });
    }
    setSearching(false);
  };

  const confirmDeleteBatch = async () => {
    if (!deleteModal || deleteConfirm !== 'DELETE') return;
    setDeleting(true);
    try {
      await axios.post(`${API}/qr-codes/delete-batches`, { batch_ids: [deleteModal.batch_id], confirm: 'DELETE' }, { withCredentials: true });
      setDeleteModal(null); setDeleteConfirm('');
      fetchData();
    } catch (e) { alert(e.response?.data?.detail || 'Error deleting batch'); }
    setDeleting(false);
  };

  const saveBatchPoints = async () => {
    if (!editModal) return;
    setSavingPoints(true);
    try {
      const res = await axios.put(`${API}/qr-codes/batch/${editModal.batch_id}/points`, { points: parseInt(editPoints) }, { withCredentials: true });
      alert(`✅ Updated points to ${res.data.points_per_coupon} for ${res.data.coupons_updated.toLocaleString()} codes`);
      setEditModal(null);
      fetchData();
    } catch (e) { alert(e.response?.data?.detail || 'Error updating points'); }
    setSavingPoints(false);
  };


  const generateQRCodes = async () => {
    if (!selectedSku) { alert('Please select an SKU'); return; }
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/qr-codes/generate`, { sku_code: selectedSku, count }, { withCredentials: true });
      alert(`✅ Generated ${res.data.count.toLocaleString()} QR codes!\nBatch ID: ${res.data.batch_id}`);
      setGenerateModal(false);
      fetchData();
    } catch (e) { alert(e.response?.data?.detail || 'Error generating QR codes'); }
    setGenerating(false);
  };

  const exportBatch = async (batchId) => {
    try {
      const res = await axios.get(`${API}/qr-codes/export/${batchId}`, { withCredentials: true });
      setExportModal(res.data);
    } catch (e) { alert('Error exporting'); }
  };

  const previewQRCodes = async (batchId) => {
    setPreviewLoading(true);
    try {
      const res = await axios.get(`${API}/qr-codes/preview/${batchId}?count=12`, { withCredentials: true });
      setPreviewModal(res.data);
    } catch (e) { alert('Error loading preview'); }
    setPreviewLoading(false);
  };

  const downloadQRImages = async (batchId, offset = 0, limit = 100) => {
    setDownloadingZip(batchId);
    try {
      const response = await axios.get(`${API}/qr-codes/batch-images/${batchId}?offset=${offset}&limit=${limit}`, {
        responseType: 'blob',
        withCredentials: true
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `qr_images_${batchId}_${offset}_${limit}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { alert('Error downloading ZIP'); }
    setDownloadingZip(null);
  };

  const downloadCSV = () => {
    if (!exportModal) return;
    const csv = 'Coupon Code,SKU Code,Status\n' + exportModal.coupons.map(c => `${c.coupon_code},${c.sku_code},${c.is_used ? 'Used' : 'Available'}`).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `qr_codes_${exportModal.batch_id}.csv`;
    a.click();
  };

  return (
    <DashboardLayout>
      <div className="page-header">
        <h1 className="page-title">🔲 QR Code Generator</h1>
        <button className="btn btn-primary" onClick={() => setGenerateModal(true)}>🆕 Generate QR Codes</button>
      </div>
      <div className="page-content">
        {/* Stats (true totals across ALL batches) */}
        <div className="bento-grid bento-grid-4" style={{ marginBottom: '1.5rem' }}>
          <div className="stat-card"><span className="stat-label">Total Batches</span><span className="stat-value">{(summary.total_batches || 0).toLocaleString()}</span></div>
          <div className="stat-card"><span className="stat-label">Total Codes Generated</span><span className="stat-value highlight">{(summary.total_codes || 0).toLocaleString()}</span></div>
          <div className="stat-card"><span className="stat-label">Total Used</span><span className="stat-value">{(summary.total_used || 0).toLocaleString()}</span></div>
          <div className="stat-card"><span className="stat-label">Available</span><span className="stat-value" style={{ color: 'var(--success)' }}>{(summary.total_available || 0).toLocaleString()}</span></div>
        </div>

        {/* Coupon Search (#4) */}
        <div className="card" style={{ marginBottom: '1.5rem', padding: '1rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '240px' }}>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', display: 'block' }}>🔍 Search Coupon / QR Code</label>
              <input className="input" data-testid="coupon-search-input" placeholder="e.g. HPA-XXXX-XXXX-XXXX" value={searchCode}
                onChange={e => setSearchCode(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') searchCoupon(); }} />
            </div>
            <button className="btn btn-primary" data-testid="coupon-search-btn" onClick={searchCoupon} disabled={searching || !searchCode.trim()}>
              {searching ? '⏳' : '🔍'} Search
            </button>
            {searchResult && <button className="btn btn-secondary" onClick={() => { setSearchResult(null); setSearchCode(''); }}>Clear</button>}
          </div>
          {searchResult && (
            <div data-testid="coupon-search-result" style={{ marginTop: '1rem', padding: '0.75rem', borderRadius: 8, background: searchResult.found ? '#E8F5E9' : '#FEF2F2', borderLeft: `4px solid ${searchResult.found ? 'var(--success)' : 'var(--hp-red)'}` }}>
              {searchResult.found ? (
                <div style={{ fontSize: '0.8rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.5rem' }}>
                  <div><strong>Code:</strong> <span className="mono">{searchResult.coupon.coupon_code}</span></div>
                  <div><strong>SKU:</strong> {searchResult.coupon.sku_name || searchResult.coupon.sku_code}</div>
                  <div><strong>Batch:</strong> <span className="mono" style={{ fontSize: '0.7rem' }}>{searchResult.coupon.batch_id}</span></div>
                  <div><strong>Points:</strong> ⭐ {searchResult.coupon.points_awarded}</div>
                  <div><strong>Status:</strong> {searchResult.coupon.is_used ? <span className="badge badge-warning">Used</span> : <span className="badge badge-success">Available</span>}</div>
                  {searchResult.coupon.is_used && <div><strong>Used by:</strong> {searchResult.coupon.used_by_name || '-'} ({searchResult.coupon.used_by_phone || '-'})</div>}
                </div>
              ) : <div style={{ fontSize: '0.85rem', color: 'var(--hp-red)', fontWeight: 600 }}>❌ {searchResult.error}</div>}
            </div>
          )}
        </div>


        {/* WhatsApp Flow Info */}
        <div className="insight-card info" style={{ marginBottom: '1.5rem' }}>
          <div className="insight-icon"><QrCode size={20} /></div>
          <div className="insight-content">
            <div className="insight-title">📱 QR Codes Open WhatsApp</div>
            <div className="insight-message">
              When scanned, QR codes redirect to WhatsApp with the coupon code pre-filled. 
              User sends the message → Bot validates → Points credited instantly!
            </div>
          </div>
        </div>

        {/* Batches Table */}
        <div className="table-container">
          {loading ? <div className="loading-container"><div className="spinner"></div></div> : (
            <table className="table">
              <thead>
                <tr>
                  <th>Batch ID</th>
                  <th>SKU</th>
                  <th>Total Codes</th>
                  <th>Used</th>
                  <th>Available</th>
                  <th>Points/Code</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {batches.map(b => (
                  <tr key={b.batch_id}>
                    <td className="mono" style={{ fontWeight: 600, fontSize: '0.7rem' }}>{b.batch_id}</td>
                    <td>{b.sku_name || b.sku_code}</td>
                    <td style={{ fontWeight: 700 }}>{b.count?.toLocaleString()}</td>
                    <td style={{ color: 'var(--warning)' }}>{b.used_count?.toLocaleString() || 0}</td>
                    <td style={{ color: 'var(--success)', fontWeight: 700 }}>{b.remaining?.toLocaleString() || 0}</td>
                    <td>⭐ {b.points_per_coupon}</td>
                    <td>{new Date(b.created_at).toLocaleDateString()}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                        <button className="btn btn-secondary" style={{ fontSize: '0.65rem', padding: '0.25rem 0.5rem' }} onClick={() => previewQRCodes(b.batch_id)}>👁️ Preview</button>
                        <button className="btn btn-secondary" style={{ fontSize: '0.65rem', padding: '0.25rem 0.5rem' }} onClick={() => exportBatch(b.batch_id)}>📄 CSV</button>
                        <button 
                          className="btn btn-primary" 
                          style={{ fontSize: '0.65rem', padding: '0.25rem 0.5rem' }} 
                          onClick={() => downloadQRImages(b.batch_id, 0, 100)}
                          disabled={downloadingZip === b.batch_id}
                        >
                          {downloadingZip === b.batch_id ? '⏳' : '🖼️'} Images
                        </button>
                        <button className="btn btn-secondary" data-testid={`edit-points-${b.batch_id}`} style={{ fontSize: '0.65rem', padding: '0.25rem 0.5rem' }} onClick={() => { setEditModal(b); setEditPoints(b.points_per_coupon || 0); }}>✏️ Value</button>
                        <button className="btn" data-testid={`delete-batch-${b.batch_id}`} style={{ fontSize: '0.65rem', padding: '0.25rem 0.5rem', background: 'var(--hp-red, #e11d48)', color: '#fff' }} onClick={() => { setDeleteModal(b); setDeleteConfirm(''); }}>🗑️ Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination (#4) */}
        {!loading && totalBatches > LIMIT && (
          <div data-testid="qr-pagination" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              Showing {((page - 1) * LIMIT) + 1}–{Math.min(page * LIMIT, totalBatches)} of {totalBatches.toLocaleString()} batches
            </span>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <button className="btn btn-secondary" data-testid="qr-prev-page" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>← Prev</button>
              <span style={{ fontSize: '0.8rem' }}>Page {page} / {Math.ceil(totalBatches / LIMIT)}</span>
              <button className="btn btn-secondary" data-testid="qr-next-page" disabled={page >= Math.ceil(totalBatches / LIMIT)} onClick={() => setPage(p => p + 1)}>Next →</button>
            </div>
          </div>
        )}

        {/* Edit Value Modal (#5) */}
        {editModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div className="card" style={{ width: '100%', maxWidth: '420px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h3 style={{ margin: 0 }}>✏️ Edit QR Code Value</h3>
                <button onClick={() => setEditModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.5rem' }}>×</button>
              </div>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
                Batch <span className="mono">{editModal.batch_id}</span> · {editModal.sku_name || editModal.sku_code} · {editModal.count?.toLocaleString()} codes
              </p>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', display: 'block' }}>Points per code</label>
              <input className="input" data-testid="edit-points-input" type="number" min={0} value={editPoints} onChange={e => setEditPoints(e.target.value)} />
              <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', margin: '0.5rem 0 1rem' }}>This updates the point value for all {editModal.count?.toLocaleString()} generated codes in this batch.</p>
              <button className="btn btn-primary" data-testid="save-points-btn" style={{ width: '100%' }} onClick={saveBatchPoints} disabled={savingPoints || editPoints === '' || parseInt(editPoints) < 0}>
                {savingPoints ? '⏳ Saving...' : '💾 Save Value'}
              </button>
            </div>
          </div>
        )}

        {/* Delete Batch Modal (#2) */}
        {deleteModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div className="card" style={{ width: '100%', maxWidth: '440px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ margin: 0, color: 'var(--hp-red, #e11d48)' }}>🗑️ Delete Batch</h3>
                <button onClick={() => setDeleteModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.5rem' }}>×</button>
              </div>
              <div style={{ padding: '0.75rem', background: '#FEF2F2', borderRadius: 8, borderLeft: '4px solid var(--hp-red, #e11d48)', marginBottom: '1rem', fontSize: '0.8rem' }}>
                This will permanently delete batch <span className="mono">{deleteModal.batch_id}</span> and its <strong>{deleteModal.count?.toLocaleString()} coupons/QR codes</strong>. Already-earned points are not affected. This cannot be undone.
              </div>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', display: 'block' }}>Type <strong>DELETE</strong> to confirm</label>
              <input className="input" data-testid="delete-confirm-input" value={deleteConfirm} onChange={e => setDeleteConfirm(e.target.value)} placeholder="DELETE" />
              <button className="btn" data-testid="confirm-delete-btn" style={{ width: '100%', marginTop: '1rem', background: 'var(--hp-red, #e11d48)', color: '#fff' }} onClick={confirmDeleteBatch} disabled={deleting || deleteConfirm !== 'DELETE'}>
                {deleting ? '⏳ Deleting...' : '🗑️ Delete Permanently'}
              </button>
            </div>
          </div>
        )}

        {/* Generate Modal */}
        {generateModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div className="card" style={{ width: '100%', maxWidth: '450px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h3 style={{ margin: 0 }}>🆕 Generate QR Codes</h3>
                <button onClick={() => setGenerateModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.5rem' }}>×</button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', display: 'block' }}>Select SKU *</label>
                  <select className="input" value={selectedSku} onChange={e => setSelectedSku(e.target.value)}>
                    <option value="">-- Select SKU --</option>
                    {skus.filter(s => s.is_active).map(s => (
                      <option key={s.sku_code} value={s.sku_code}>{s.sku_code} - {s.name} (⭐{s.points})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', display: 'block' }}>Number of QR Codes</label>
                  <input className="input" type="number" value={count} onChange={e => setCount(Math.min(250000, parseInt(e.target.value) || 0))} min={1} max={250000} />
                  <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>Max: 2,50,000 codes per batch</p>
                </div>
                <div style={{ padding: '1rem', background: 'linear-gradient(135deg, #FEF3C7, #FEF2F2)', borderRadius: 12 }}>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Total Points Value</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--hp-red)' }}>
                    ⭐ {(count * (skus.find(s => s.sku_code === selectedSku)?.points || 0)).toLocaleString()}
                  </div>
                </div>
                <button className="btn btn-primary" style={{ width: '100%' }} onClick={generateQRCodes} disabled={generating || !selectedSku}>
                  {generating ? '⏳ Generating...' : `🔲 Generate ${count.toLocaleString()} QR Codes`}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* QR Preview Modal */}
        {previewModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
            <div className="card" style={{ width: '100%', maxWidth: '900px', maxHeight: '90vh', overflow: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ margin: 0 }}>🔲 QR Code Preview - {previewModal.sku_name}</h3>
                <button onClick={() => setPreviewModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.5rem' }}>×</button>
              </div>
              
              <div style={{ marginBottom: '1rem', padding: '1rem', background: 'linear-gradient(135deg, #FEF3C7, #FEF2F2)', borderRadius: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
                  <div><strong>Batch:</strong> {previewModal.batch_id}</div>
                  <div><strong>Total Codes:</strong> {previewModal.total_in_batch?.toLocaleString()}</div>
                  <div><strong>Points:</strong> ⭐ {previewModal.points_per_coupon}</div>
                </div>
              </div>

              {/* WhatsApp Flow Info */}
              <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#E8F5E9', borderRadius: 8, borderLeft: '4px solid #25D366' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, color: '#25D366', marginBottom: '0.25rem' }}>
                  <span>📱</span> QR Code → WhatsApp Flow
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                  Scan → Opens WhatsApp → Coupon pre-filled → User sends → Points credited!
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <button className="btn btn-primary" onClick={() => downloadQRImages(previewModal.batch_id, 0, 100)} disabled={downloadingZip}>
                  {downloadingZip ? '⏳ Downloading...' : '📥 Download First 100 QR Images (ZIP)'}
                </button>
                <button className="btn btn-secondary" style={{ marginLeft: '0.5rem' }} onClick={() => downloadQRImages(previewModal.batch_id, 0, 500)} disabled={downloadingZip}>
                  📥 Download 500 Images
                </button>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '1rem' }}>
                {previewModal.qr_codes?.map(qr => (
                  <div key={qr.coupon_code} style={{ background: 'var(--bg-tertiary)', borderRadius: 12, padding: '0.5rem', textAlign: 'center' }}>
                    <img 
                      src={`data:image/png;base64,${qr.image_base64}`} 
                      alt={qr.coupon_code} 
                      style={{ width: '100%', maxWidth: '160px', borderRadius: 8 }}
                    />
                    <div style={{ fontSize: '0.7rem', fontFamily: 'monospace', marginTop: '0.5rem', wordBreak: 'break-all' }}>{qr.coupon_code}</div>
                    <a href={`${API}/qr-codes/image/${qr.coupon_code}`} download className="btn btn-ghost" style={{ fontSize: '0.65rem', marginTop: '0.25rem' }}>📥 Download</a>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Export Modal */}
        {exportModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div className="card" style={{ width: '100%', maxWidth: '600px', maxHeight: '80vh', overflow: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ margin: 0 }}>📄 Export CSV: {exportModal.batch_id}</h3>
                <button onClick={() => setExportModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.5rem' }}>×</button>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <p><strong>SKU:</strong> {exportModal.sku_name || exportModal.sku_code}</p>
                <p><strong>Total Codes:</strong> {exportModal.total?.toLocaleString()}</p>
              </div>
              <button className="btn btn-primary" onClick={downloadCSV} style={{ marginBottom: '1rem' }}>📥 Download CSV</button>
              <div style={{ maxHeight: '300px', overflow: 'auto', fontSize: '0.75rem', fontFamily: 'monospace', background: 'var(--bg-tertiary)', padding: '0.5rem', borderRadius: 8 }}>
                {exportModal.coupons.slice(0, 100).map(c => <div key={c.coupon_code}>{c.coupon_code}</div>)}
                {exportModal.coupons.length > 100 && <div style={{ color: 'var(--text-muted)' }}>...and {exportModal.coupons.length - 100} more</div>}
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

// ============== PAYOUTS APPROVAL PAGE ==============
const PayoutsPage = () => {
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending_approval');

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/points/payout-history?status=${filter}`, { withCredentials: true });
      setRequests(res.data.requests || []);
      setStats(res.data.stats || {});
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [filter]);

  const handleAction = async (requestId, action, reason = '') => {
    try {
      if (action === 'approve' || action === 'reject') {
        await axios.post(`${API}/points/approve/${requestId}`, { action, reason }, { withCredentials: true });
      } else if (action === 'process') {
        await axios.post(`${API}/points/process-payout/${requestId}`, {}, { withCredentials: true });
      }
      fetchData();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  return (
    <DashboardLayout>
      <div className="page-header"><h1 className="page-title">💰 Payouts & Approvals</h1></div>
      <div className="page-content">
        {/* Stats */}
        <div className="bento-grid bento-grid-4" style={{ marginBottom: '1.5rem' }}>
          <div className="stat-card" style={{ cursor: 'pointer', border: filter === 'pending_approval' ? '2px solid var(--hp-red)' : '' }} onClick={() => setFilter('pending_approval')}>
            <span className="stat-label">⏳ Pending Approval</span>
            <span className="stat-value highlight">{stats.pending || 0}</span>
          </div>
          <div className="stat-card" style={{ cursor: 'pointer', border: filter === 'approved' ? '2px solid var(--success)' : '' }} onClick={() => setFilter('approved')}>
            <span className="stat-label">✅ Approved</span>
            <span className="stat-value" style={{ color: 'var(--success)' }}>{stats.approved || 0}</span>
          </div>
          <div className="stat-card" style={{ cursor: 'pointer', border: filter === 'processed' ? '2px solid var(--accent-blue)' : '' }} onClick={() => setFilter('processed')}>
            <span className="stat-label">💸 Processed</span>
            <span className="stat-value">{stats.processed || 0}</span>
          </div>
          <div className="stat-card" style={{ cursor: 'pointer', border: filter === 'rejected' ? '2px solid var(--error)' : '' }} onClick={() => setFilter('rejected')}>
            <span className="stat-label">❌ Rejected</span>
            <span className="stat-value" style={{ color: 'var(--error)' }}>{stats.rejected || 0}</span>
          </div>
        </div>

        {/* Table */}
        <div className="table-container">
          {loading ? <div className="loading-container"><div className="spinner"></div></div> : (
            <table className="table">
              <thead>
                <tr>
                  <th>Request ID</th>
                  <th>User</th>
                  <th>Phone</th>
                  <th>Country</th>
                  <th>Points</th>
                  <th>Amount</th>
                  <th>UPI ID</th>
                  <th>KYC</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.map(r => (
                  <tr key={r.request_id}>
                    <td className="mono" style={{ fontSize: '0.7rem' }}>{r.request_id}</td>
                    <td>{r.user_name}</td>
                    <td>{r.phone}</td>
                    <td data-testid={`request-country-${r.request_id}`}>
                      <span className="badge badge-neutral">{getCountryFromPhone(r.phone).flag} {getCountryFromPhone(r.phone).name}</span>
                    </td>
                    <td style={{ fontWeight: 700 }}>⭐ {r.points?.toLocaleString()}</td>
                    <td style={{ color: 'var(--success)', fontWeight: 700 }}>₹{r.amount?.toLocaleString()}</td>
                    <td className="mono">{r.upi_id}</td>
                    <td><span className={`badge ${r.kyc_status === 'verified' ? 'badge-success' : 'badge-warning'}`}>{r.kyc_status}</span></td>
                    <td><span className={`badge ${r.payout_status === 'pending_approval' ? 'badge-warning' : r.payout_status === 'approved' ? 'badge-success' : r.payout_status === 'processed' ? 'badge-neutral' : 'badge-error'}`}>{r.payout_status}</span></td>
                    <td>
                      {r.payout_status === 'pending_approval' && (
                        <div style={{ display: 'flex', gap: '0.25rem' }}>
                          <button className="btn btn-primary" style={{ fontSize: '0.65rem', padding: '0.25rem 0.5rem' }} onClick={() => handleAction(r.request_id, 'approve')}>✅ Approve</button>
                          <button className="btn btn-secondary" style={{ fontSize: '0.65rem', padding: '0.25rem 0.5rem', color: 'var(--error)' }} onClick={() => handleAction(r.request_id, 'reject', 'Rejected by admin')}>❌</button>
                        </div>
                      )}
                      {r.payout_status === 'approved' && (
                        <button className="btn btn-primary" style={{ fontSize: '0.65rem', padding: '0.25rem 0.5rem' }} onClick={() => handleAction(r.request_id, 'process')}>💸 Process</button>
                      )}
                    </td>
                  </tr>
                ))}
                {requests.length === 0 && <tr><td colSpan={9} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No requests found</td></tr>}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

// ============== REWARDS CATALOG PAGE (WITH IMAGES) ==============
const RewardsCatalogPage = () => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [importModal, setImportModal] = useState(false);
  const [importData, setImportData] = useState('');
  const [importing, setImporting] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [itemsRes, catsRes] = await Promise.all([
        axios.get(`${API}/store/items${selectedCategory ? `?category_id=${selectedCategory}` : ''}`, { withCredentials: true }),
        axios.get(`${API}/store/categories`, { withCredentials: true })
      ]);
      setItems(itemsRes.data.items || []);
      setCategories(catsRes.data.categories || []);
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [selectedCategory]);

  const handleImport = async () => {
    setImporting(true);
    try {
      const parsed = JSON.parse(importData);
      const itemsArray = Array.isArray(parsed) ? parsed : [parsed];
      const res = await axios.post(`${API}/rewards-catalog/import`, { items: itemsArray }, { withCredentials: true });
      alert(`✅ Imported: ${res.data.imported}, Updated: ${res.data.updated}`);
      setImportModal(false);
      setImportData('');
      fetchData();
    } catch (e) { alert('Invalid JSON or import error'); }
    setImporting(false);
  };

  return (
    <DashboardLayout>
      <div className="page-header">
        <h1 className="page-title">🎁 Rewards Catalog</h1>
        <button className="btn btn-primary" onClick={() => setImportModal(true)}>📥 Import Catalog</button>
      </div>
      <div className="page-content">
        {/* Category Filter */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <button className={`btn ${selectedCategory === '' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setSelectedCategory('')}>All</button>
          {categories.map(c => (
            <button key={c.category_id} className={`btn ${selectedCategory === c.category_id ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setSelectedCategory(c.category_id)}>{c.name}</button>
          ))}
        </div>

        {/* Items Grid */}
        {loading ? <div className="loading-container"><div className="spinner"></div></div> : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '1rem' }}>
            {items.map(item => (
              <div key={item.item_id} className="card" style={{ padding: '0', overflow: 'hidden' }}>
                <div style={{ height: '150px', background: 'var(--bg-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                  {item.image_url ? (
                    <img src={item.image_url} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} onError={e => { e.target.style.display = 'none'; }} />
                  ) : (
                    <span style={{ fontSize: '3rem' }}>📦</span>
                  )}
                </div>
                <div style={{ padding: '1rem' }}>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>{item.brand}</div>
                  <div style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.5rem', lineHeight: 1.3 }}>{item.name}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <span style={{ textDecoration: 'line-through', color: 'var(--text-muted)', fontSize: '0.8rem' }}>₹{item.mrp?.toLocaleString()}</span>
                    <span style={{ color: 'var(--hp-red)', fontWeight: 700, fontSize: '1.1rem' }}>⭐ {item.points_required?.toLocaleString()}</span>
                  </div>
                  <span className={`badge ${item.is_active ? 'badge-success' : 'badge-error'}`}>{item.stock > 0 ? `${item.stock} in stock` : 'Out of stock'}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Import Modal */}
        {importModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div className="card" style={{ width: '100%', maxWidth: '700px', maxHeight: '90vh', overflow: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ margin: 0 }}>📥 Import Rewards Catalog</h3>
                <button onClick={() => setImportModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.5rem' }}>×</button>
              </div>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
                Paste JSON array from CSV. Required: <code>PRODUCT CODE</code>, <code>PRODUCT NAME</code>, <code>MRP</code>, <code>Reward Price</code> (points), <code>Image URL</code>
              </p>
              <textarea className="input" rows={15} value={importData} onChange={e => setImportData(e.target.value)} placeholder='[{"PRODUCT CODE": "HOK-001", "PRODUCT NAME": "Vacuum Cleaner", "BRAND NAME": "AGARO", "MRP": 2999, "Reward Price": 2282, "Image URL": "https://..."}]' style={{ fontFamily: 'monospace', fontSize: '0.7rem' }} />
              <button className="btn btn-primary" style={{ width: '100%', marginTop: '1rem' }} onClick={handleImport} disabled={importing}>
                {importing ? '⏳ Importing...' : '📥 Import Catalog'}
              </button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

// ============== LEGACY PAGES ==============
const SKUsPage = () => { const [skus, setSkus] = useState([]); const [loading, setLoading] = useState(true); useEffect(() => { axios.get(`${API}/skus`, { withCredentials: true }).then(r => setSkus(r.data.skus || [])).finally(() => setLoading(false)); }, []); return <DashboardLayout><div className="page-header"><h1 className="page-title">SKUs</h1></div><div className="page-content"><div className="table-container">{loading ? <div className="loading-container"><div className="spinner"></div></div> : <table className="table"><thead><tr><th>SKU</th><th>Name</th><th>Points</th><th>MRP</th></tr></thead><tbody>{skus.map(s => <tr key={s.sku_code}><td className="mono">{s.sku_code}</td><td>{s.name}</td><td style={{ color: 'var(--success)' }}>{s.points}</td><td>₹{s.mrp}</td></tr>)}</tbody></table>}</div></div></DashboardLayout>; };
const CouponsPage = () => { const [coupons, setCoupons] = useState([]); const [loading, setLoading] = useState(true); useEffect(() => { axios.get(`${API}/coupons`, { withCredentials: true }).then(r => setCoupons(r.data.coupons || [])).finally(() => setLoading(false)); }, []); return <DashboardLayout><div className="page-header"><h1 className="page-title">Coupons</h1></div><div className="page-content"><div className="table-container">{loading ? <div className="loading-container"><div className="spinner"></div></div> : <table className="table"><thead><tr><th>Code</th><th>SKU</th><th>Status</th></tr></thead><tbody>{coupons.map(c => <tr key={c.coupon_code}><td className="mono">{c.coupon_code}</td><td>{c.sku_code}</td><td><span className={`badge ${c.is_used ? 'badge-warning' : 'badge-success'}`}>{c.is_used ? 'Used' : 'Available'}</span></td></tr>)}</tbody></table>}</div></div></DashboardLayout>; };
const ScansPage = () => { const [scans, setScans] = useState([]); const [loading, setLoading] = useState(true); useEffect(() => { axios.get(`${API}/scans`, { withCredentials: true }).then(r => setScans(r.data.scans || [])).finally(() => setLoading(false)); }, []); return <DashboardLayout><div className="page-header"><h1 className="page-title">Scans</h1></div><div className="page-content"><div className="table-container">{loading ? <div className="loading-container"><div className="spinner"></div></div> : <table className="table"><thead><tr><th>Coupon</th><th>User</th><th>Points</th><th>Time</th></tr></thead><tbody>{scans.map(s => <tr key={s.scan_id}><td className="mono">{s.coupon_code}</td><td>{s.user_name}</td><td style={{ color: 'var(--success)' }}>+{s.points_awarded}</td><td style={{ fontSize: '0.8rem' }}>{new Date(s.created_at).toLocaleString()}</td></tr>)}</tbody></table>}</div></div></DashboardLayout>; };
const TransactionsPage = () => { const [txns, setTxns] = useState([]); const [loading, setLoading] = useState(true); useEffect(() => { axios.get(`${API}/transactions`, { withCredentials: true }).then(r => setTxns(r.data.transactions || [])).finally(() => setLoading(false)); }, []); return <DashboardLayout><div className="page-header"><h1 className="page-title">Ledger</h1></div><div className="page-content"><div className="table-container">{loading ? <div className="loading-container"><div className="spinner"></div></div> : <table className="table"><thead><tr><th>User</th><th>Type</th><th>Points</th><th>Description</th></tr></thead><tbody>{txns.map(t => <tr key={t.ledger_id}><td>{t.user_name}</td><td><span className={`badge ${t.entry_type === 'earn' ? 'badge-success' : 'badge-warning'}`}>{t.entry_type}</span></td><td style={{ fontWeight: 600, color: t.points_delta > 0 ? 'var(--success)' : 'var(--error)' }}>{t.points_delta > 0 ? '+' : ''}{t.points_delta}</td><td>{t.description}</td></tr>)}</tbody></table>}</div></div></DashboardLayout>; };
const OrdersPage = () => { const [orders, setOrders] = useState([]); const [loading, setLoading] = useState(true); useEffect(() => { axios.get(`${API}/orders`, { withCredentials: true }).then(r => setOrders(r.data.orders || [])).finally(() => setLoading(false)); }, []); const updateStatus = async (id, s) => { await axios.put(`${API}/orders/${id}/status`, { status: s }, { withCredentials: true }); window.location.reload(); }; return <DashboardLayout><div className="page-header"><h1 className="page-title">Orders</h1></div><div className="page-content"><div className="table-container">{loading ? <div className="loading-container"><div className="spinner"></div></div> : <table className="table"><thead><tr><th>Order</th><th>User</th><th>Country</th><th>Item</th><th>Status</th><th>Actions</th></tr></thead><tbody>{orders.map(o => <tr key={o.order_id}><td className="mono" style={{ fontSize: '0.7rem' }}>{o.order_id}</td><td>{o.user_name}</td><td data-testid={`order-country-${o.order_id}`}><span className="badge badge-neutral">{getCountryFromPhone(o.phone || o.shipping_phone).flag} {getCountryFromPhone(o.phone || o.shipping_phone).name}</span></td><td>{o.item_name || o.items?.map(i => i.name).join(', ')}</td><td><span className="badge badge-warning">{o.status}</span></td><td><select className="input" style={{ width: 'auto', fontSize: '0.7rem', padding: '0.25rem' }} value={o.status} onChange={e => updateStatus(o.order_id, e.target.value)}><option value="confirmed">Confirmed</option><option value="shipped">Shipped</option><option value="delivered">Delivered</option></select></td></tr>)}</tbody></table>}</div></div></DashboardLayout>; };
const StoreItemsPage = () => { const [items, setItems] = useState([]); const [loading, setLoading] = useState(true); useEffect(() => { axios.get(`${API}/store/items`, { withCredentials: true }).then(r => setItems(r.data.items || [])).finally(() => setLoading(false)); }, []); return <DashboardLayout><div className="page-header"><h1 className="page-title">Store Items</h1></div><div className="page-content"><div className="table-container">{loading ? <div className="loading-container"><div className="spinner"></div></div> : <table className="table"><thead><tr><th>Item</th><th>Type</th><th>Points</th><th>Stock</th></tr></thead><tbody>{items.map(i => <tr key={i.item_id}><td>{i.name}</td><td><span className="badge badge-neutral">{i.item_type}</span></td><td style={{ color: 'var(--hp-red)', fontWeight: 600 }}>{i.points_required}</td><td>{i.stock}</td></tr>)}</tbody></table>}</div></div></DashboardLayout>; };
const SchemesPage = () => { const [schemes, setSchemes] = useState([]); const [loading, setLoading] = useState(true); useEffect(() => { axios.get(`${API}/schemes`, { withCredentials: true }).then(r => setSchemes(r.data.schemes || [])).finally(() => setLoading(false)); }, []); return <DashboardLayout><div className="page-header"><h1 className="page-title">Schemes</h1></div><div className="page-content"><div className="table-container">{loading ? <div className="loading-container"><div className="spinner"></div></div> : <table className="table"><thead><tr><th>Name</th><th>Type</th><th>Value</th><th>Status</th></tr></thead><tbody>{schemes.map(s => <tr key={s.scheme_id}><td>{s.name}</td><td>{s.scheme_type}</td><td>{s.scheme_type === 'multiplier' ? `${s.points_multiplier}x` : `+${s.bonus_points}`}</td><td><span className={`badge ${s.is_active ? 'badge-success' : 'badge-neutral'}`}>{s.is_active ? 'Active' : 'Inactive'}</span></td></tr>)}</tbody></table>}</div></div></DashboardLayout>; };
// Enhanced AI Insights Page
const InsightsPage = () => {
  const [insights, setInsights] = useState([]);
  const [detailedData, setDetailedData] = useState(null);
  const [aiRecommendations, setAiRecommendations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('insights');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [insightsRes, detailedRes] = await Promise.all([
          axios.get(`${API}/insights`, { withCredentials: true }),
          axios.get(`${API}/insights/detailed`, { withCredentials: true })
        ]);
        setInsights(insightsRes.data.insights || []);
        setDetailedData(detailedRes.data);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchData();
  }, []);

  const generateAiInsights = async () => {
    setAiLoading(true);
    try {
      const res = await axios.post(`${API}/insights/generate-ai`, {}, { withCredentials: true });
      setAiRecommendations(res.data.ai_insights);
    } catch (e) { console.error(e); }
    setAiLoading(false);
  };

  const getInsightIcon = (type) => {
    switch(type) {
      case 'alert': return <AlertTriangle size={24} style={{ color: '#DC2626' }} />;
      case 'warning': return <AlertTriangle size={24} style={{ color: '#F59E0B' }} />;
      case 'success': return <TrendingUp size={24} style={{ color: '#10B981' }} />;
      default: return <Info size={24} style={{ color: '#3B82F6' }} />;
    }
  };

  const getInsightBg = (type) => {
    switch(type) {
      case 'alert': return 'linear-gradient(135deg, #FEE2E2, #FECACA)';
      case 'warning': return 'linear-gradient(135deg, #FEF3C7, #FDE68A)';
      case 'success': return 'linear-gradient(135deg, #D1FAE5, #A7F3D0)';
      default: return 'linear-gradient(135deg, #DBEAFE, #BFDBFE)';
    }
  };

  return (
    <DashboardLayout>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
        <h1 className="page-title"><Sparkles size={24} style={{ color: 'var(--accent-gold)' }} /> AI Insights & Analytics</h1>
        <button className="btn btn-primary" onClick={generateAiInsights} disabled={aiLoading} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {aiLoading ? <RefreshCw size={16} className="spin" /> : <Lightbulb size={16} />}
          {aiLoading ? 'Generating...' : 'Generate AI Recommendations'}
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
        {[
          { id: 'insights', label: 'Platform Insights', icon: <AlertTriangle size={16} /> },
          { id: 'analytics', label: 'Analytics', icon: <BarChart3 size={16} /> },
          { id: 'ai', label: 'AI Recommendations', icon: <Sparkles size={16} /> }
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`btn ${activeTab === tab.id ? 'btn-primary' : 'btn-secondary'}`} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      <div className="page-content">
        {loading ? <div className="loading-container"><div className="spinner"></div></div> : (
          <>
            {/* Platform Insights Tab */}
            {activeTab === 'insights' && (
              <div style={{ display: 'grid', gap: '1rem' }}>
                {insights.length > 0 ? insights.map((insight, idx) => (
                  <div key={idx} className="card" style={{ background: getInsightBg(insight.type), border: 'none' }}>
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                      <div style={{ padding: '0.75rem', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                        {getInsightIcon(insight.type)}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                          <span style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)', background: 'white', padding: '0.25rem 0.5rem', borderRadius: '4px' }}>{insight.category}</span>
                          <span className={`badge ${insight.priority === 'high' ? 'badge-danger' : insight.priority === 'medium' ? 'badge-warning' : 'badge-success'}`}>{insight.priority} priority</span>
                        </div>
                        <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.5rem' }}>{insight.title}</h3>
                        <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>{insight.message}</p>
                        {insight.recommendation && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: 'var(--hp-red)' }}>
                            <Lightbulb size={14} />
                            <strong>Recommendation:</strong> {insight.recommendation}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )) : (
                  <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
                    <Sparkles size={48} style={{ color: 'var(--success)', marginBottom: '1rem' }} />
                    <h3 style={{ color: 'var(--success)' }}>All Systems Normal</h3>
                    <p style={{ color: 'var(--text-muted)' }}>No critical issues detected. Keep up the good work!</p>
                  </div>
                )}
              </div>
            )}

            {/* Analytics Tab */}
            {activeTab === 'analytics' && detailedData && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
                {/* New Users */}
                <div className="card">
                  <h3 style={{ fontSize: '1rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Users size={18} /> New Users (30 Days)
                  </h3>
                  <div style={{ fontSize: '2.5rem', fontWeight: 800, color: 'var(--hp-red)' }}>{detailedData.new_users_30d}</div>
                </div>

                {/* Top Users */}
                <div className="card">
                  <h3 style={{ fontSize: '1rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Crown size={18} style={{ color: 'var(--accent-gold)' }} /> Top Earners
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {detailedData.top_users?.slice(0, 5).map((user, idx) => (
                      <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', background: idx === 0 ? 'linear-gradient(135deg, #FEF3C7, #FDE68A)' : 'var(--bg-tertiary)', borderRadius: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ width: '24px', height: '24px', borderRadius: '50%', background: idx === 0 ? 'var(--accent-gold)' : 'var(--text-muted)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700 }}>{idx + 1}</span>
                          <span style={{ fontSize: '0.85rem' }}>{user.name || 'Unknown'}</span>
                        </div>
                        <span style={{ fontWeight: 700, color: 'var(--hp-red)' }}>{user.total_points_earned?.toLocaleString()} pts</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* SKU Performance */}
                <div className="card">
                  <h3 style={{ fontSize: '1rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Package size={18} /> Top Products
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {detailedData.sku_performance?.slice(0, 5).map((sku, idx) => (
                      <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', background: 'var(--bg-tertiary)', borderRadius: '8px' }}>
                        <span style={{ fontSize: '0.85rem', fontFamily: 'monospace' }}>{sku._id}</span>
                        <span style={{ fontWeight: 600 }}>{sku.scans} scans</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Daily Scans Chart */}
                <div className="card" style={{ gridColumn: 'span 2' }}>
                  <h3 style={{ fontSize: '1rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <TrendingUp size={18} /> Daily Scans Trend
                  </h3>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: '150px' }}>
                    {detailedData.daily_scans?.slice(-14).map((day, idx) => {
                      const maxCount = Math.max(...detailedData.daily_scans.map(d => d.count));
                      const height = maxCount > 0 ? (day.count / maxCount) * 100 : 0;
                      return (
                        <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <div style={{ width: '100%', height: `${height}%`, minHeight: '4px', background: 'linear-gradient(180deg, var(--hp-red), #B91C1C)', borderRadius: '4px 4px 0 0' }} title={`${day._id}: ${day.count} scans`} />
                          <span style={{ fontSize: '0.6rem', color: 'var(--text-muted)', marginTop: '4px' }}>{day._id?.slice(-2)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* AI Recommendations Tab */}
            {activeTab === 'ai' && (
              <div>
                {aiRecommendations ? (
                  <div style={{ display: 'grid', gap: '1.5rem' }}>
                    {/* Summary */}
                    {aiRecommendations.summary && (
                      <div className="card" style={{ background: 'linear-gradient(135deg, #EDE9FE, #DDD6FE)', border: 'none' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <Sparkles size={32} style={{ color: '#7C3AED' }} />
                          <div>
                            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.25rem' }}>AI Summary</h3>
                            <p style={{ fontSize: '0.95rem' }}>{aiRecommendations.summary}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Recommendations */}
                    {aiRecommendations.recommendations?.length > 0 && (
                      <div className="card">
                        <h3 style={{ fontSize: '1rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <Lightbulb size={18} style={{ color: 'var(--accent-gold)' }} /> Strategic Recommendations
                        </h3>
                        <div style={{ display: 'grid', gap: '1rem' }}>
                          {aiRecommendations.recommendations.map((rec, idx) => (
                            <div key={idx} style={{ padding: '1rem', background: 'var(--bg-tertiary)', borderRadius: '12px', borderLeft: `4px solid ${rec.impact === 'high' ? 'var(--hp-red)' : rec.impact === 'medium' ? 'var(--warning)' : 'var(--success)'}` }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                                <h4 style={{ fontSize: '0.95rem', fontWeight: 700 }}>{rec.title}</h4>
                                <span className={`badge ${rec.impact === 'high' ? 'badge-danger' : rec.impact === 'medium' ? 'badge-warning' : 'badge-success'}`}>{rec.impact} impact</span>
                              </div>
                              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{rec.description}</p>
                              {rec.category && <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.5rem', display: 'block' }}>Category: {rec.category}</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Campaign Ideas */}
                    {aiRecommendations.campaign_ideas?.length > 0 && (
                      <div className="card">
                        <h3 style={{ fontSize: '1rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <Target size={18} style={{ color: 'var(--hp-red)' }} /> Campaign Ideas
                        </h3>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                          {aiRecommendations.campaign_ideas.map((idea, idx) => (
                            <span key={idx} style={{ padding: '0.5rem 1rem', background: 'linear-gradient(135deg, #FEE2E2, #FECACA)', borderRadius: '20px', fontSize: '0.85rem' }}>{idea}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Risk Alerts */}
                    {aiRecommendations.risk_alerts?.length > 0 && (
                      <div className="card" style={{ background: 'linear-gradient(135deg, #FEE2E2, #FECACA)', border: 'none' }}>
                        <h3 style={{ fontSize: '1rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <AlertTriangle size={18} style={{ color: '#DC2626' }} /> Risk Alerts
                        </h3>
                        <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                          {aiRecommendations.risk_alerts.map((alert, idx) => (
                            <li key={idx} style={{ marginBottom: '0.5rem', fontSize: '0.9rem' }}>{alert}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Raw response fallback */}
                    {aiRecommendations.raw_response && (
                      <div className="card">
                        <h3 style={{ fontSize: '1rem', marginBottom: '1rem' }}>AI Response</h3>
                        <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem', background: 'var(--bg-tertiary)', padding: '1rem', borderRadius: '8px' }}>{aiRecommendations.raw_response}</pre>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
                    <Sparkles size={48} style={{ color: 'var(--text-muted)', marginBottom: '1rem' }} />
                    <h3>Generate AI Recommendations</h3>
                    <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem' }}>Click the button above to get AI-powered insights based on your platform data.</p>
                    <button className="btn btn-primary" onClick={generateAiInsights} disabled={aiLoading}>
                      {aiLoading ? 'Generating...' : 'Generate Now'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

// Help Center Page
const HelpCenterPage = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    axios.get(`${API}/help-center`).then(r => {
      setSections(r.data.sections || []);
      if (r.data.sections?.length > 0) setActiveSection(r.data.sections[0]);
    }).finally(() => setLoading(false));
  }, []);

  const getIcon = (iconName) => {
    const icons = {
      LayoutDashboard: <LayoutDashboard size={20} />,
      Users: <Users size={20} />,
      Package: <Package size={20} />,
      QrCode: <QrCode size={20} />,
      Gift: <Gift size={20} />,
      Truck: <Truck size={20} />,
      MessageCircle: <MessageCircle size={20} />,
      Sparkles: <Sparkles size={20} />
    };
    return icons[iconName] || <BookOpen size={20} />;
  };

  const filteredSections = sections.filter(s => 
    s.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="page-header">
        <h1 className="page-title"><HelpCircle size={24} style={{ color: 'var(--hp-red)' }} /> Help Center</h1>
        <p style={{ color: 'var(--text-muted)', marginTop: '0.25rem' }}>Learn how to use every feature of the platform</p>
      </div>

      {/* Search */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ position: 'relative', maxWidth: '400px' }}>
          <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input
            className="input"
            type="text"
            placeholder="Search help topics..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{ paddingLeft: '40px' }}
          />
        </div>
      </div>

      {loading ? <div className="loading-container"><div className="spinner"></div></div> : (
        <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '1.5rem', minHeight: '600px' }}>
          {/* Sidebar */}
          <div className="card" style={{ padding: '0.5rem', height: 'fit-content' }}>
            <div style={{ padding: '0.75rem', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Topics</div>
            {filteredSections.map(section => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  width: '100%',
                  padding: '0.875rem 1rem',
                  border: 'none',
                  background: activeSection?.id === section.id ? 'linear-gradient(135deg, #FEE2E2, #FECACA)' : 'transparent',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ color: activeSection?.id === section.id ? 'var(--hp-red)' : 'var(--text-muted)' }}>
                  {getIcon(section.icon)}
                </div>
                <div>
                  <div style={{ fontSize: '0.9rem', fontWeight: activeSection?.id === section.id ? 700 : 500 }}>{section.title}</div>
                </div>
              </button>
            ))}
          </div>

          {/* Content */}
          {activeSection && (
            <div>
              {/* Header */}
              <div className="card" style={{ marginBottom: '1rem', background: 'linear-gradient(135deg, #FEE2E2, #FECACA)', border: 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ padding: '1rem', background: 'white', borderRadius: '12px', color: 'var(--hp-red)' }}>
                    {getIcon(activeSection.icon)}
                  </div>
                  <div>
                    <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.25rem' }}>{activeSection.title}</h2>
                    <p style={{ color: 'var(--text-secondary)' }}>{activeSection.description}</p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="card" style={{ marginBottom: '1rem' }}>
                <div style={{ 
                  fontSize: '0.95rem', 
                  lineHeight: '1.8',
                  '& h2': { fontSize: '1.25rem', fontWeight: 700, marginTop: '1.5rem', marginBottom: '0.75rem' },
                  '& h3': { fontSize: '1.1rem', fontWeight: 600, marginTop: '1.25rem', marginBottom: '0.5rem' },
                  '& ul, & ol': { paddingLeft: '1.5rem', marginBottom: '1rem' },
                  '& li': { marginBottom: '0.5rem' },
                  '& code': { background: 'var(--bg-tertiary)', padding: '0.125rem 0.375rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.85rem' },
                  '& strong': { fontWeight: 600 }
                }}>
                  {activeSection.content.split('\n').map((line, idx) => {
                    if (line.startsWith('## ')) return <h2 key={idx} style={{ fontSize: '1.25rem', fontWeight: 700, marginTop: '1.5rem', marginBottom: '0.75rem', color: 'var(--hp-red)' }}>{line.replace('## ', '')}</h2>;
                    if (line.startsWith('### ')) return <h3 key={idx} style={{ fontSize: '1rem', fontWeight: 600, marginTop: '1rem', marginBottom: '0.5rem' }}>{line.replace('### ', '')}</h3>;
                    if (line.startsWith('- ')) return <div key={idx} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', paddingLeft: '1rem' }}><span style={{ color: 'var(--hp-red)' }}>•</span><span>{line.replace('- ', '')}</span></div>;
                    if (line.startsWith('1. ') || line.startsWith('2. ') || line.startsWith('3. ') || line.startsWith('4. ')) {
                      const num = line.charAt(0);
                      return <div key={idx} style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.5rem', paddingLeft: '1rem' }}><span style={{ color: 'var(--hp-red)', fontWeight: 700 }}>{num}.</span><span>{line.slice(3)}</span></div>;
                    }
                    if (line.includes('`')) {
                      const parts = line.split('`');
                      return <p key={idx} style={{ marginBottom: '0.5rem' }}>{parts.map((part, i) => i % 2 === 1 ? <code key={i} style={{ background: 'var(--bg-tertiary)', padding: '0.125rem 0.5rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.85rem' }}>{part}</code> : part)}</p>;
                    }
                    if (line.trim() === '') return <div key={idx} style={{ height: '0.5rem' }} />;
                    return <p key={idx} style={{ marginBottom: '0.5rem' }}>{line}</p>;
                  })}
                </div>
              </div>

              {/* FAQs */}
              {activeSection.faqs?.length > 0 && (
                <div className="card">
                  <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <MessageCircle size={18} /> Frequently Asked Questions
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {activeSection.faqs.map((faq, idx) => (
                      <div key={idx} style={{ padding: '1rem', background: 'var(--bg-tertiary)', borderRadius: '8px' }}>
                        <div style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                          <span style={{ color: 'var(--hp-red)' }}>Q:</span> {faq.q}
                        </div>
                        <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                          <span style={{ color: 'var(--success)' }}>A:</span> {faq.a}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </DashboardLayout>
  );
};

// ============== SETTINGS / DANGER ZONE (DATA PURGE) ==============
const DEFAULT_KEEP_SKUS = "STRONGWELD50ML\nSTRONGWELD100ML\nSTRONGWELD200ML";
const DEFAULT_KEEP_BATCHES = "batch_20260612_104226_f3188b\nbatch_20260612_104146_78b688\nbatch_20260612_104052_373c4d";

const SettingsPage = () => {
  const [keepSkus, setKeepSkus] = useState(DEFAULT_KEEP_SKUS);
  const [keepBatches, setKeepBatches] = useState(DEFAULT_KEEP_BATCHES);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [done, setDone] = useState(null);

  const parseLines = (s) => s.split('\n').map(x => x.trim()).filter(Boolean);

  const callPurge = async (dryRun) => {
    setError(''); setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/purge-data`, {
        confirm_token: 'PURGE-PLATFORM-DATA',
        dry_run: dryRun,
        keep_skus: parseLines(keepSkus),
        keep_batches: parseLines(keepBatches),
        delete_store_catalog: true,
        delete_schemes: true,
      }, { withCredentials: true });
      if (dryRun) { setReport(res.data); setDone(null); }
      else { setDone(res.data); setReport(null); setConfirmText(''); }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Request failed. Are you logged in as super admin?');
    } finally { setLoading(false); }
  };

  const rowStyle = { display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', borderBottom: '1px solid var(--border-default)', fontSize: '0.85rem' };

  return (
    <DashboardLayout>
      <div className="page-header">
        <h1 className="page-title"><Settings size={24} style={{ color: 'var(--hp-red)' }} /> Settings</h1>
        <p style={{ color: 'var(--text-muted)', marginTop: '0.25rem' }}>Platform configuration and maintenance</p>
      </div>

      <div className="card" style={{ border: '2px solid var(--hp-red)', maxWidth: '720px' }} data-testid="danger-zone-card">
        <h3 style={{ fontSize: '1.05rem', fontWeight: 700, marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--hp-red)' }}>
          <AlertTriangle size={20} /> Danger Zone — Reset Platform Data
        </h3>
        <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
          Permanently deletes ALL users, points, ledger, orders, scans, KYC, conversations, rewards catalog and schemes — and every SKU/batch/coupon EXCEPT the keep-list below. Admin accounts are always preserved. Always run <b>Preview</b> first.
        </p>

        <label style={{ fontSize: '0.8rem', fontWeight: 600 }}>Keep SKUs (one per line)</label>
        <textarea className="input" rows={3} value={keepSkus} onChange={e => setKeepSkus(e.target.value)} data-testid="keep-skus-input" style={{ marginBottom: '0.75rem', fontFamily: 'monospace', fontSize: '0.85rem' }} />

        <label style={{ fontSize: '0.8rem', fontWeight: 600 }}>Keep Batch IDs (one per line)</label>
        <textarea className="input" rows={3} value={keepBatches} onChange={e => setKeepBatches(e.target.value)} data-testid="keep-batches-input" style={{ marginBottom: '1rem', fontFamily: 'monospace', fontSize: '0.85rem' }} />

        {error && <div style={{ color: 'var(--hp-red)', fontSize: '0.85rem', marginBottom: '0.75rem' }} data-testid="purge-error">{error}</div>}

        <button className="btn btn-secondary" onClick={() => callPurge(true)} disabled={loading} data-testid="purge-preview-btn">
          {loading ? 'Loading…' : 'Preview (Dry Run)'}
        </button>

        {report && (
          <div style={{ marginTop: '1rem', padding: '1rem', background: 'var(--bg-tertiary)', borderRadius: '8px' }} data-testid="purge-preview-report">
            <div style={{ fontWeight: 700, marginBottom: '0.5rem' }}>Preview — this will happen on execute:</div>
            <div style={{ fontWeight: 600, color: 'var(--success)', marginBottom: '0.25rem' }}>✅ Surviving</div>
            {Object.entries(report.surviving || {}).map(([k, v]) => (
              <div key={k} style={rowStyle}><span>{k}</span><b>{v}</b></div>
            ))}
            <div style={{ fontWeight: 600, color: 'var(--hp-red)', margin: '0.75rem 0 0.25rem' }}>🗑️ To be deleted</div>
            {Object.entries(report.deleted || {}).map(([k, v]) => (
              <div key={k} style={rowStyle}><span>{k}</span><b>{v}</b></div>
            ))}

            <div style={{ marginTop: '1rem', padding: '0.75rem', border: '1px dashed var(--hp-red)', borderRadius: '8px' }}>
              <div style={{ fontSize: '0.8rem', marginBottom: '0.5rem' }}>To confirm, type <code style={{ background: 'var(--bg-secondary)', padding: '0.1rem 0.4rem', borderRadius: '4px' }}>PURGE-PLATFORM-DATA</code></div>
              <input className="input" value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder="Type to confirm" data-testid="purge-confirm-input" style={{ marginBottom: '0.75rem' }} />
              <button className="btn" style={{ background: 'var(--hp-red)', color: 'white' }} disabled={loading || confirmText !== 'PURGE-PLATFORM-DATA'} onClick={() => callPurge(false)} data-testid="purge-execute-btn">
                {loading ? 'Purging…' : 'Execute Purge (permanent)'}
              </button>
            </div>
          </div>
        )}

        {done && (
          <div style={{ marginTop: '1rem', padding: '1rem', background: 'var(--bg-tertiary)', borderRadius: '8px', border: '1px solid var(--success)' }} data-testid="purge-done">
            <div style={{ fontWeight: 700, color: 'var(--success)', marginBottom: '0.5rem' }}>✅ {done.message}</div>
            {Object.entries(done.surviving || {}).map(([k, v]) => (
              <div key={k} style={rowStyle}><span>survived: {k}</span><b>{v}</b></div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};


// ============== USER STORE WITH OTP LOGIN ==============
const UserStoreLogin = () => {
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('phone'); // 'phone' or 'otp'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Check for existing session
  useEffect(() => {
    const token = localStorage.getItem('user_session');
    if (token) {
      axios.get(`${API}/user/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => navigate(`/rewards/${res.data.phone}`))
        .catch(() => localStorage.removeItem('user_session'));
    }
  }, [navigate]);

  const sendOtp = async () => {
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/user/send-otp`, { phone });
      setStep('otp');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to send OTP');
    }
    setLoading(false);
  };

  const verifyOtp = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/user/verify-otp`, { phone, otp });
      localStorage.setItem('user_session', res.data.session_token);
      navigate(`/rewards/${res.data.user.phone}`);
    } catch (e) {
      setError(e.response?.data?.detail || 'Invalid OTP');
    }
    setLoading(false);
  };

  return (
    <div className="login-container">
      <div className="login-bg"></div>
      <div className="login-card slide-up" style={{ maxWidth: '400px' }}>
        <img src="https://customer-assets.emergentagent.com/job_hp-adhesive/artifacts/wrwk26nv_logo-01.png" alt="HP Adhesives" style={{ height: '40px', marginBottom: '1rem' }} />
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem' }}>🛍️ Rewards Store</h1>
        <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '1.5rem' }}>
          Login with your registered mobile number to access rewards
        </p>

        {error && (
          <div style={{ background: '#FEE2E2', color: '#DC2626', padding: '0.75rem', borderRadius: '8px', fontSize: '0.875rem', marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        {step === 'phone' ? (
          <>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>Mobile Number</label>
              <input
                className="input"
                type="tel"
                placeholder="Enter 10-digit mobile number"
                value={phone}
                onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                style={{ fontSize: '1rem', padding: '0.875rem' }}
                data-testid="phone-input"
              />
            </div>
            <button
              className="btn btn-primary"
              style={{ width: '100%', padding: '0.875rem', fontSize: '1rem' }}
              onClick={sendOtp}
              disabled={phone.length !== 10 || loading}
              data-testid="send-otp-btn"
            >
              {loading ? 'Sending...' : '📱 Send OTP via WhatsApp'}
            </button>
          </>
        ) : (
          <>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>Enter OTP</label>
              <input
                className="input"
                type="text"
                placeholder="Enter 6-digit OTP"
                value={otp}
                onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                style={{ fontSize: '1.25rem', padding: '0.875rem', textAlign: 'center', letterSpacing: '0.5rem' }}
                data-testid="otp-input"
              />
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                OTP sent to +91 {phone} via WhatsApp
              </p>
            </div>
            <button
              className="btn btn-primary"
              style={{ width: '100%', padding: '0.875rem', fontSize: '1rem', marginBottom: '0.5rem' }}
              onClick={verifyOtp}
              disabled={otp.length !== 6 || loading}
              data-testid="verify-otp-btn"
            >
              {loading ? 'Verifying...' : '✅ Verify & Login'}
            </button>
            <button
              className="btn btn-secondary"
              style={{ width: '100%', padding: '0.75rem' }}
              onClick={() => { setStep('phone'); setOtp(''); setError(''); }}
            >
              ← Change Number
            </button>
          </>
        )}

        <p style={{ marginTop: '2rem', fontSize: '0.75rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
          HP Adhesives Rewards
        </p>
      </div>
    </div>
  );
};

// ============== USER REWARDS PORTAL (AUTHENTICATED) ==============
const UserRewardsPortal = () => {
  const { phone } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('shop'); // 'shop', 'orders', 'wallet'
  const [selectedCategory, setSelectedCategory] = useState('');
  const [orderModal, setOrderModal] = useState(null);
  const [shipping, setShipping] = useState({ name: '', address: '', city: '', state: '', pincode: '', contact_phone: '' });
  const [ordering, setOrdering] = useState(false);

  const token = localStorage.getItem('user_session');

  useEffect(() => {
    if (!token) {
      navigate('/shop');
      return;
    }

    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [userRes, catsRes, itemsRes, ordersRes] = await Promise.all([
          axios.get(`${API}/user/me`, { headers }),
          axios.get(`${API}/store/categories`),
          axios.get(`${API}/store/items${selectedCategory ? `?category_id=${selectedCategory}` : ''}`),
          axios.get(`${API}/user/orders`, { headers })
        ]);
        setUser(userRes.data);
        setCategories(catsRes.data.categories || []);
        setItems(itemsRes.data.items || []);
        setOrders(ordersRes.data.orders || []);
        setShipping(s => ({ ...s, name: userRes.data.name || '', contact_phone: userRes.data.phone?.slice(-10) || '' }));
      } catch (e) {
        if (e.response?.status === 401) {
          localStorage.removeItem('user_session');
          navigate('/shop');
        }
      }
      setLoading(false);
    };
    fetchData();
  }, [token, navigate, selectedCategory]);

  const placeOrder = async () => {
    if (!orderModal) return;
    setOrdering(true);
    try {
      await axios.post(`${API}/store/order`, { phone, item_id: orderModal.item_id, shipping }, { headers: { Authorization: `Bearer ${token}` } });
      alert('🎉 Order placed! You will receive confirmation on WhatsApp.');
      setOrderModal(null);
      // Refresh data
      const [userRes, ordersRes] = await Promise.all([
        axios.get(`${API}/user/me`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/user/orders`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setUser(userRes.data);
      setOrders(ordersRes.data.orders || []);
    } catch (e) { alert(e.response?.data?.detail || 'Failed to place order'); }
    setOrdering(false);
  };

  const logout = () => {
    localStorage.removeItem('user_session');
    navigate('/shop');
  };

  if (loading) return <div className="wallet-page"><div className="loading-container"><div className="spinner"></div></div></div>;
  if (!user) return <div className="wallet-page" style={{ textAlign: 'center', paddingTop: '3rem' }}><h2>Session Expired</h2><button className="btn btn-primary" onClick={() => navigate('/shop')}>Login Again</button></div>;

  return (
    <div className="wallet-page" style={{ paddingBottom: '5rem', maxWidth: '500px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <img src="https://customer-assets.emergentagent.com/job_hp-adhesive/artifacts/wrwk26nv_logo-01.png" alt="HP" style={{ height: '28px' }} />
        <button onClick={logout} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.8rem' }}>
          <LogOut size={16} /> Logout
        </button>
      </div>

      {/* User Info Card */}
      <div className="card" style={{ background: 'linear-gradient(135deg, #E31837, #B91C1C)', color: 'white', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: '0.8rem', opacity: 0.9 }}>Welcome, {user.name?.split(' ')[0] || 'User'}</div>
            <div style={{ fontSize: '2rem', fontWeight: 800 }}>{user.current_balance?.toLocaleString()} <span style={{ fontSize: '0.875rem', fontWeight: 400 }}>points</span></div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '0.7rem', opacity: 0.8 }}>Total Earned</div>
            <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{user.total_points_earned?.toLocaleString()}</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        {[
          { id: 'shop', icon: <ShoppingBag size={16} />, label: 'Shop' },
          { id: 'orders', icon: <Truck size={16} />, label: 'Orders' },
          { id: 'wallet', icon: <Wallet size={16} />, label: 'Wallet' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`btn ${activeTab === tab.id ? 'btn-primary' : 'btn-secondary'}`}
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Shop Tab */}
      {activeTab === 'shop' && (
        <>
          {/* Categories */}
          <div style={{ display: 'flex', gap: '0.5rem', overflowX: 'auto', marginBottom: '1rem', paddingBottom: '0.5rem' }}>
            <button onClick={() => setSelectedCategory('')} className={`btn ${selectedCategory === '' ? 'btn-primary' : 'btn-secondary'}`} style={{ flexShrink: 0, fontSize: '0.75rem' }}>All</button>
            {categories.map(cat => (
              <button key={cat.category_id} onClick={() => setSelectedCategory(cat.category_id)} className={`btn ${selectedCategory === cat.category_id ? 'btn-primary' : 'btn-secondary'}`} style={{ flexShrink: 0, fontSize: '0.75rem' }}>
                {cat.name}
              </button>
            ))}
          </div>

          {/* Items Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
            {items.map(item => (
              <div key={item.item_id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ height: '100px', background: 'var(--bg-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {item.image_url ? (
                    <img src={item.image_url} alt={item.name} style={{ maxHeight: '90px', maxWidth: '100%', objectFit: 'contain' }} />
                  ) : (
                    <Gift size={32} style={{ color: 'var(--text-muted)' }} />
                  )}
                </div>
                <div style={{ padding: '0.75rem' }}>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{item.brand_name}</div>
                  <div style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem', height: '2.4em', overflow: 'hidden' }}>{item.name}</div>
                  {item.mrp > 0 && <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textDecoration: 'line-through' }}>₹{item.mrp}</div>}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem' }}>
                    <span style={{ color: 'var(--hp-red)', fontWeight: 700 }}>{item.points_required} pts</span>
                    <button
                      onClick={() => { if (user.current_balance >= item.points_required) setOrderModal(item); }}
                      className="btn btn-primary"
                      style={{ fontSize: '0.65rem', padding: '0.35rem 0.6rem' }}
                      disabled={user.current_balance < item.points_required}
                    >
                      {user.current_balance >= item.points_required ? 'Get' : '🔒'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Orders Tab */}
      {activeTab === 'orders' && (
        <div>
          {orders.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
              <Truck size={48} style={{ color: 'var(--text-muted)', marginBottom: '1rem' }} />
              <p>No orders yet. Start shopping!</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {orders.map(order => (
                <div key={order.order_id} className="card" style={{ padding: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{order.item_name}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Order #{order.order_id?.slice(-8)}</div>
                    </div>
                    <span className={`badge ${order.status === 'delivered' ? 'badge-success' : order.status === 'shipped' ? 'badge-warning' : 'badge-neutral'}`}>
                      {order.status}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    {order.tracking_number && <div>🚚 Tracking: {order.tracking_number}</div>}
                    <div>📅 {new Date(order.created_at).toLocaleDateString()}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Wallet Tab */}
      {activeTab === 'wallet' && (
        <div>
          <div className="card" style={{ marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '0.9rem', marginBottom: '1rem' }}>Points Summary</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', textAlign: 'center' }}>
              <div>
                <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--hp-red)' }}>{user.current_balance?.toLocaleString()}</div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Available</div>
              </div>
              <div>
                <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--success)' }}>{user.total_points_earned?.toLocaleString()}</div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Earned</div>
              </div>
              <div>
                <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--warning)' }}>{user.total_points_redeemed?.toLocaleString()}</div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Redeemed</div>
              </div>
            </div>
          </div>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', textAlign: 'center' }}>
            💡 Scan more HP Adhesive products to earn points!
          </p>
        </div>
      )}

      {/* Order Modal */}
      {orderModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
          <div className="card" style={{ width: '100%', maxWidth: '400px', maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0 }}>🎁 Confirm Order</h3>
              <button onClick={() => setOrderModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.5rem' }}>×</button>
            </div>
            
            <div style={{ padding: '1rem', background: 'var(--bg-tertiary)', borderRadius: '8px', marginBottom: '1rem' }}>
              <div style={{ fontWeight: 600 }}>{orderModal.name}</div>
              <div style={{ color: 'var(--hp-red)', fontWeight: 700 }}>{orderModal.points_required} points</div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <input className="input" placeholder="Full Name" value={shipping.name} onChange={e => setShipping({ ...shipping, name: e.target.value })} />
              <input className="input" placeholder="Contact Phone" value={shipping.contact_phone} onChange={e => setShipping({ ...shipping, contact_phone: e.target.value })} />
              <textarea className="input" placeholder="Complete Address" value={shipping.address} onChange={e => setShipping({ ...shipping, address: e.target.value })} rows={2} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                <input className="input" placeholder="City" value={shipping.city} onChange={e => setShipping({ ...shipping, city: e.target.value })} />
                <input className="input" placeholder="State" value={shipping.state} onChange={e => setShipping({ ...shipping, state: e.target.value })} />
              </div>
              <input className="input" placeholder="PIN Code" value={shipping.pincode} onChange={e => setShipping({ ...shipping, pincode: e.target.value })} />
            </div>

            <button
              className="btn btn-primary"
              style={{ width: '100%', marginTop: '1rem', padding: '0.875rem' }}
              onClick={placeOrder}
              disabled={ordering || !shipping.name || !shipping.address || !shipping.city}
            >
              {ordering ? 'Processing...' : `✅ Redeem for ${orderModal.points_required} points`}
            </button>
          </div>
        </div>
      )}

      {/* Footer */}
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: 'white', padding: '0.75rem', borderTop: '1px solid var(--border)', textAlign: 'center' }}>
        <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
          HP Adhesives Rewards
        </p>
      </div>
    </div>
  );
};

// Public Store with AI Recommendations
const PublicStore = () => {
  const { phone } = useParams();
  const [user, setUser] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gamification, setGamification] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [orderModal, setOrderModal] = useState(null);
  const [shipping, setShipping] = useState({ name: '', address: '', city: '', state: '', pincode: '', phone: '' });
  const [ordering, setOrdering] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [userRes, recsRes, catsRes, itemsRes, gamRes] = await Promise.all([
          axios.get(`${API}/public/wallet/${phone}`),
          axios.get(`${API}/store/recommendations/${phone}`),
          axios.get(`${API}/store/categories`),
          axios.get(`${API}/store/items${selectedCategory ? `?category_id=${selectedCategory}` : ''}`),
          axios.get(`${API}/gamification/user/${phone}`)
        ]);
        setUser(userRes.data.user);
        setRecommendations(recsRes.data.recommendations || []);
        setCategories(catsRes.data.categories || []);
        setItems(itemsRes.data.items || []);
        setGamification(gamRes.data);
      } catch (e) {}
      setLoading(false);
    };
    fetchData();
  }, [phone, selectedCategory]);

  const placeOrder = async () => {
    if (!orderModal) return;
    setOrdering(true);
    try {
      await axios.post(`${API}/store/order`, { phone, item_id: orderModal.item_id, shipping });
      alert('🎉 Order placed! Check WhatsApp for confirmation.');
      setOrderModal(null);
      const userRes = await axios.get(`${API}/public/wallet/${phone}`);
      setUser(userRes.data.user);
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    setOrdering(false);
  };

  if (loading) return <div className="wallet-page"><div className="loading-container"><div className="spinner"></div></div></div>;
  if (!user) return <div className="wallet-page" style={{ textAlign: 'center', paddingTop: '3rem' }}><div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔍</div><h2>User Not Found</h2></div>;

  return (
    <div className="wallet-page" style={{ paddingBottom: '2rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <img src="https://customer-assets.emergentagent.com/job_hp-adhesive/artifacts/wrwk26nv_logo-01.png" alt="HP" style={{ height: '28px' }} />
        <Link to={`/wallet/${phone}`} style={{ color: 'var(--hp-red)', textDecoration: 'none', fontSize: '0.8rem', fontWeight: 600 }}>💰 My Wallet →</Link>
      </div>

      {/* Balance & Tier Banner */}
      <div className="gamification-card" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{gamification?.tier?.icon} {gamification?.tier?.name} Member</div>
            <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--hp-red)' }}>{user.current_balance?.toLocaleString()} <span style={{ fontSize: '0.875rem', fontWeight: 400 }}>points</span></div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Points Multiplier</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--warning)' }}>{gamification?.points_multiplier || 1}x</div>
          </div>
        </div>
      </div>

      {/* AI Recommendations */}
      {recommendations.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          {recommendations.slice(0, 2).map((rec, i) => (
            <div key={i} style={{ marginBottom: '1rem' }}>
              <h3 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Sparkles size={14} style={{ color: 'var(--accent-gold)' }} /> {rec.section}
              </h3>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>{rec.reason}</div>
              <div style={{ display: 'flex', gap: '0.75rem', overflowX: 'auto', paddingBottom: '0.5rem' }}>
                {rec.items.slice(0, 4).map(item => (
                  <div key={item.item_id} className="reward-card" style={{ minWidth: '150px' }}>
                    <div style={{ fontSize: '1.5rem', marginBottom: '0.25rem' }}>{item.item_type === 'voucher' ? '🎫' : '📦'}</div>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>{item.name}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: 'var(--hp-red)', fontWeight: 700, fontSize: '0.9rem' }}>{item.points_required} pts</span>
                      <button 
                        onClick={() => { if (user.current_balance >= item.points_required) { setOrderModal(item); setShipping({ ...shipping, name: user.name, phone }); } }} 
                        className="btn btn-primary" 
                        style={{ fontSize: '0.6rem', padding: '0.3rem 0.6rem' }} 
                        disabled={user.current_balance < item.points_required}
                      >
                        {user.current_balance >= item.points_required ? '🎁 Get' : '🔒'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Categories */}
      <div style={{ display: 'flex', gap: '0.5rem', overflowX: 'auto', marginBottom: '1rem', paddingBottom: '0.5rem' }}>
        <button onClick={() => setSelectedCategory('')} className={`btn ${selectedCategory === '' ? 'btn-primary' : 'btn-secondary'}`} style={{ flexShrink: 0, fontSize: '0.75rem', padding: '0.5rem 1rem' }}>🎯 All</button>
        {categories.map(cat => (
          <button key={cat.category_id} onClick={() => setSelectedCategory(cat.category_id)} className={`btn ${selectedCategory === cat.category_id ? 'btn-primary' : 'btn-secondary'}`} style={{ flexShrink: 0, fontSize: '0.75rem', padding: '0.5rem 1rem' }}>
            {cat.name}
          </button>
        ))}
      </div>

      {/* Store Items */}
      <h3 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.75rem' }}>🛍️ All Rewards ({items.length} items)</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
        {items.map(item => (
          <div key={item.item_id} className="reward-card" style={{ padding: 0, overflow: 'hidden' }}>
            {/* Product Image */}
            <div style={{ height: '100px', background: 'var(--bg-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              {item.image_url ? (
                <img 
                  src={item.image_url} 
                  alt={item.name} 
                  style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '0.25rem' }} 
                  onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                />
              ) : null}
              <div style={{ display: item.image_url ? 'none' : 'flex', fontSize: '2.5rem', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}>
                {item.item_type === 'voucher' ? '🎫' : item.item_type === 'digital' ? '📱' : '📦'}
              </div>
            </div>
            
            {/* Product Info */}
            <div style={{ padding: '0.75rem' }}>
              <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>{item.brand}</div>
              <div style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.5rem', lineHeight: 1.3, height: '2.6em', overflow: 'hidden' }}>{item.name}</div>
              
              {/* MRP & Points */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                {item.mrp > 0 && (
                  <span style={{ textDecoration: 'line-through', color: 'var(--text-muted)', fontSize: '0.7rem' }}>₹{item.mrp?.toLocaleString()}</span>
                )}
                <span style={{ color: 'var(--hp-red)', fontWeight: 700, fontSize: '0.95rem' }}>⭐ {item.points_required?.toLocaleString()}</span>
              </div>
              
              <button 
                onClick={() => { if (user.current_balance >= item.points_required) { setOrderModal(item); setShipping({ ...shipping, name: user.name, phone }); } }} 
                className="btn btn-primary" 
                style={{ width: '100%', fontSize: '0.7rem', padding: '0.5rem' }} 
                disabled={user.current_balance < item.points_required}
              >
                {user.current_balance >= item.points_required ? '🎁 Redeem Now' : `Need ${(item.points_required - user.current_balance).toLocaleString()} more`}
              </button>
            </div>
          </div>
        ))}
      </div>

      {items.length === 0 && (
        <div className="empty-state">
          <div className="empty-state-icon">🏪</div>
          <p>No rewards available right now</p>
          <p style={{ fontSize: '0.75rem' }}>Check back soon for exciting offers!</p>
        </div>
      )}

      {/* Order Modal */}
      {orderModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
          <div style={{ background: 'var(--bg-primary)', borderRadius: 20, padding: '1.5rem', width: '100%', maxWidth: '400px', maxHeight: '90vh', overflow: 'auto', boxShadow: 'var(--shadow-lg)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem' }}>🎁 Redeem: {orderModal.name}</h3>
              <button onClick={() => setOrderModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.5rem' }}>×</button>
            </div>
            
            <div style={{ marginBottom: '1rem', padding: '1rem', background: 'linear-gradient(135deg, #FEF3C7, #FEF2F2)', borderRadius: 12, textAlign: 'center' }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Points Required</div>
              <div style={{ fontSize: '1.75rem', fontWeight: 800, color: 'var(--hp-red)' }}>{orderModal.points_required}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--success)', marginTop: '0.25rem' }}>✓ You have {user.current_balance} points</div>
            </div>
            
            {orderModal.item_type === 'physical' && (
              <>
                <h4 style={{ margin: '1rem 0 0.75rem', fontSize: '0.9rem' }}>📍 Shipping Details</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <input type="text" className="input" placeholder="Full Name" value={shipping.name} onChange={e => setShipping({ ...shipping, name: e.target.value })} />
                  <textarea className="input" placeholder="Complete Address" rows={2} value={shipping.address} onChange={e => setShipping({ ...shipping, address: e.target.value })} style={{ resize: 'none' }} />
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                    <input type="text" className="input" placeholder="City" value={shipping.city} onChange={e => setShipping({ ...shipping, city: e.target.value })} />
                    <input type="text" className="input" placeholder="PIN Code" value={shipping.pincode} onChange={e => setShipping({ ...shipping, pincode: e.target.value })} />
                  </div>
                  <input type="text" className="input" placeholder="Contact Number" value={shipping.phone} onChange={e => setShipping({ ...shipping, phone: e.target.value })} />
                </div>
              </>
            )}
            
            <button onClick={placeOrder} className="btn btn-primary" style={{ width: '100%', marginTop: '1.5rem', padding: '0.875rem' }} disabled={ordering}>
              {ordering ? '⏳ Processing...' : '🎉 Confirm Order'}
            </button>
          </div>
        </div>
      )}

      {/* Footer */}
      <div style={{ textAlign: 'center', marginTop: '2rem' }}>
        <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>HP Adhesives Rewards</p>
      </div>
    </div>
  );
};

// Public Wallet
const PublicWallet = () => {
  const { phone } = useParams();
  const [data, setData] = useState(null);
  const [gamification, setGamification] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [walletRes, gamRes] = await Promise.all([
          axios.get(`${API}/public/wallet/${phone}`),
          axios.get(`${API}/gamification/user/${phone}`)
        ]);
        setData(walletRes.data);
        setGamification(gamRes.data);
      } catch (e) {}
      setLoading(false);
    };
    fetchData();
  }, [phone]);

  if (loading) return <div className="wallet-page"><div className="loading-container"><div className="spinner"></div></div></div>;
  if (!data?.user) return <div className="wallet-page" style={{ textAlign: 'center', padding: '3rem' }}><div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔍</div><h2>Wallet Not Found</h2><p style={{ color: 'var(--text-muted)' }}>Please check the phone number</p></div>;

  const { user, transactions } = data;
  const progressToNext = gamification?.next_tier ? Math.min(100, (user.total_points_earned / (user.total_points_earned + gamification.next_tier.points_needed)) * 100) : 100;

  return (
    <div className="wallet-page">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <img src="https://customer-assets.emergentagent.com/job_hp-adhesive/artifacts/wrwk26nv_logo-01.png" alt="HP" style={{ height: '32px' }} />
        <Link to={`/store/${phone}`} className="btn btn-primary" style={{ fontSize: '0.75rem', padding: '0.5rem 1rem' }}>🛍️ Store</Link>
      </div>
      
      {/* Welcome Banner */}
      <div className="wallet-header">
        <div style={{ marginBottom: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>Hey, {user.name}! 👋</div>
        <div className="wallet-balance">{user.current_balance?.toLocaleString()}</div>
        <div className="wallet-label">🌟 Reward Points</div>
        
        {/* Progress to next tier */}
        {gamification?.next_tier && (
          <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(255,255,255,0.5)', borderRadius: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: '0.5rem' }}>
              <span>{gamification?.tier?.icon} {gamification?.tier?.name}</span>
              <span>{gamification.next_tier.icon} {gamification.next_tier.name}</span>
            </div>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progressToNext}%` }}></div>
            </div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.5rem', textAlign: 'center' }}>
              {gamification.next_tier.points_needed} more points to reach {gamification.next_tier.name}!
            </div>
          </div>
        )}

        {/* Stats Row */}
        <div className="quick-stats" style={{ marginTop: '1rem' }}>
          <div className="quick-stat">
            <div className="quick-stat-value" style={{ color: 'var(--success)' }}>+{user.total_points_earned?.toLocaleString()}</div>
            <div className="quick-stat-label">Earned</div>
          </div>
          <div className="quick-stat">
            <div className="quick-stat-value" style={{ color: 'var(--warning)' }}>-{user.total_points_redeemed?.toLocaleString()}</div>
            <div className="quick-stat-label">Redeemed</div>
          </div>
          <div className="quick-stat">
            <div className="quick-stat-value">{user.total_scans || 0}</div>
            <div className="quick-stat-label">Scans</div>
          </div>
        </div>
      </div>

      {/* Tier Badge */}
      {gamification && (
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <span className="tier-badge">{gamification.tier?.icon} {gamification.tier?.name} Member • {gamification?.points_multiplier || 1}x Points</span>
        </div>
      )}

      {/* Badges Earned */}
      {gamification?.badges?.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '0.9rem', marginBottom: '0.75rem', fontWeight: 600 }}>🏅 Badges Earned</h3>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {gamification.badges.map((b, i) => (
              <span key={i} style={{ background: 'linear-gradient(135deg, #FEF3C7, #FEF2F2)', padding: '0.5rem 0.875rem', borderRadius: 20, fontSize: '0.8rem', fontWeight: 500, boxShadow: 'var(--shadow-sm)' }}>
                {b.icon} {b.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="wallet-actions">
        <Link to={`/store/${phone}`} className="wallet-action-btn">
          <div className="wallet-action-icon"><ShoppingBag size={24} /></div>
          <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>🎁 Rewards</span>
        </Link>
        <div className="wallet-action-btn">
          <div className="wallet-action-icon"><QrCode size={24} /></div>
          <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>📸 Scan</span>
        </div>
      </div>
      
      {/* Recent Transactions */}
      <div>
        <h3 style={{ fontSize: '0.9rem', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600 }}>
          <History size={16} /> Recent Activity
        </h3>
        <div className="transaction-list">
          {transactions?.length > 0 ? transactions.map(t => (
            <div key={t.ledger_id} className="transaction-item">
              <div className="transaction-info">
                <div className="transaction-desc">{t.points_delta > 0 ? '🎉' : '🛍️'} {t.description}</div>
                <div className="transaction-date">{new Date(t.created_at).toLocaleDateString()}</div>
              </div>
              <div className={`transaction-amount ${t.points_delta > 0 ? 'positive' : 'negative'}`}>
                {t.points_delta > 0 ? '+' : ''}{t.points_delta}
              </div>
            </div>
          )) : (
            <div className="empty-state">
              <div className="empty-state-icon">📭</div>
              <p>No transactions yet</p>
              <p style={{ fontSize: '0.75rem' }}>Scan your first coupon to earn points!</p>
            </div>
          )}
        </div>
      </div>

      {/* KYC Verified Badge */}
      {user.kyc_status === 'verified' && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginTop: '1.5rem', padding: '0.75rem', background: 'var(--success-light)', borderRadius: 10, color: 'var(--success)', fontSize: '0.8rem', fontWeight: 600 }}>
          <ShieldCheck size={16} /> ✅ KYC Verified Account
        </div>
      )}

      {/* Footer */}
      <div style={{ textAlign: 'center', marginTop: '2rem', paddingBottom: '1rem' }}>
        <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>HP Adhesives Rewards</p>
      </div>
    </div>
  );
};

// Router
function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardHome /></ProtectedRoute>} />
      <Route path="/dashboard/analytics" element={<ProtectedRoute permission="analytics:*"><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/dashboard/users" element={<ProtectedRoute permission="users:read"><UsersPage /></ProtectedRoute>} />
      <Route path="/dashboard/brands" element={<ProtectedRoute permission="brands:read"><BrandsPage /></ProtectedRoute>} />
      <Route path="/dashboard/sku-master" element={<ProtectedRoute permission="skus:read"><SKUMasterPage /></ProtectedRoute>} />
      <Route path="/dashboard/qr-codes" element={<ProtectedRoute permission="coupons:read"><QRCodesPage /></ProtectedRoute>} />
      <Route path="/dashboard/payouts" element={<ProtectedRoute permission="payouts:read"><PayoutsPage /></ProtectedRoute>} />
      <Route path="/dashboard/skus" element={<ProtectedRoute permission="skus:read"><SKUsPage /></ProtectedRoute>} />
      <Route path="/dashboard/coupons" element={<ProtectedRoute permission="coupons:read"><CouponsPage /></ProtectedRoute>} />
      <Route path="/dashboard/gift-cards" element={<ProtectedRoute permission="gift_cards:read"><GiftCardsPage /></ProtectedRoute>} />
      <Route path="/dashboard/scans" element={<ProtectedRoute permission="orders:read"><ScansPage /></ProtectedRoute>} />
      <Route path="/dashboard/transactions" element={<ProtectedRoute permission="ledger:read"><TransactionsPage /></ProtectedRoute>} />
      <Route path="/dashboard/orders" element={<ProtectedRoute permission="orders:read"><OrdersPage /></ProtectedRoute>} />
      <Route path="/dashboard/rewards-catalog" element={<ProtectedRoute permission="store:read"><RewardsCatalogPage /></ProtectedRoute>} />
      <Route path="/dashboard/store" element={<ProtectedRoute permission="store:read"><StoreItemsPage /></ProtectedRoute>} />
      <Route path="/dashboard/schemes" element={<ProtectedRoute permission="schemes:read"><SchemesPage /></ProtectedRoute>} />
      <Route path="/dashboard/gamification" element={<ProtectedRoute permission="gamification:read"><GamificationAdminPage /></ProtectedRoute>} />
      <Route path="/dashboard/admin-users" element={<ProtectedRoute permission="*"><AdminUsersPage /></ProtectedRoute>} />
      <Route path="/dashboard/settings" element={<ProtectedRoute permission="*"><SettingsPage /></ProtectedRoute>} />
      <Route path="/dashboard/audit-logs" element={<ProtectedRoute permission="ledger:read"><AuditLogsPage /></ProtectedRoute>} />
      <Route path="/dashboard/insights" element={<ProtectedRoute><InsightsPage /></ProtectedRoute>} />
      <Route path="/dashboard/help" element={<ProtectedRoute><HelpCenterPage /></ProtectedRoute>} />
      <Route path="/wallet/:phone" element={<PublicWallet />} />
      <Route path="/store/:phone" element={<PublicStore />} />
      <Route path="/shop" element={<UserStoreLogin />} />
      <Route path="/rewards/:phone" element={<UserRewardsPortal />} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() { return <BrowserRouter><AuthProvider><AppRouter /></AuthProvider></BrowserRouter>; }
export default App;
